// The heliocentric orrery, from the registry.
//
// Every body is a row of public/art/atlas/bodies.json: its position comes from
// the propagator at the clock's Julian date, its radius and rotation state from
// the row, and its render position from the camera-relative frame. There is no
// legibility scale left - a radius is metres and a distance is metres, which is
// what the logarithmic depth buffer and the camera-relative frame exist for.
//
// What a physical scale costs is that most bodies are far under a pixel from
// anywhere you would want to see the whole system from, so a body drawn smaller
// than `minDrawnAngularDeg` is drawn AT that size and records both numbers in
// `userData`. Nothing inflates an orbit: a dot in the right place beats a
// planet in the wrong one.

import * as THREE from 'three';

import {
  createArtificialFill, globeFillChunks, markArtificialFill,
  type ArtificialFillHandle, type ArtificialFillRef,
} from './artificialFill.ts';
import {
  createArtificialRelief, markArtificialRelief,
  type ArtificialReliefHandle, type ArtificialReliefRef,
} from './artificialRelief.ts';
import { createCoverageFill } from './coverageFill.ts';
import {
  applyBodyShadowOnly, createBodyShadows, markBodyShadow,
  type BodyShadowRef, type BodyShadowsHandle,
} from './bodyShadow.ts';
import { GROUND_METALNESS, GROUND_ROUGHNESS } from './groundBrdf.ts';
import type { CoverageFillHandle, CoverageFillRef } from './coverageFill';
import { loadFlippedBitmap } from './imageBitmap.ts';
import { createTileReliefSampler, type TileReliefSampler } from './tileRelief.ts';
import { decodeClassMapWebp } from './tileReliefDecode.ts';
import {
  applyRingShadow, createRingProfile, createRings,
  RING_FULL_ANGULAR_DIAMETER_DEG, RING_MIN_ANGULAR_DIAMETER_DEG, type RingsHandle,
} from './rings.ts';
import { declutterLabels, type LabelCandidate } from './labelLayout.ts';
import { ellipsoidGeometry, ellipsoidStandsIn } from './shapeGlobe.ts';
import { createSol, subPixelFade, type SolHandle } from './sol.ts';
import {
  createSolPhotosphere, type SolPhotosphereHandle, type SolPhotosphereReading,
} from './solPhotosphere.ts';
import {
  AU_KM, DAYS_PER_CENTURY, barycentreStep, elementsAt, heliocentric, positionFromE, stateAt, type Vec3,
} from '../world/ephemeris.ts';
import { LUNAR_ELLIPSE_DRIFT_KM_PER_DAY } from '../world/lunar.ts';
import { poleAndMeridianAt, type PoleAndMeridian } from '../world/orientation.ts';
import { devFlag, urlFlag, TEST_SWITCHES } from '../urlFlags.ts';
import type { BodyRow, Orbit } from '../world/types';
import type { WorldFrameHandle, WorldVec } from '../world/frame';
import { relinkLightKeyed } from '../world/scene.ts';
import {
  albedoColorSpace, albedoTransferOf, albedoTransferUniform, attachAlbedoTransfer, type AlbedoTransfer,
} from './albedoTransfer.ts';

const _baryStep = { position: { x: 0, y: 0, z: 0 }, velocity: { x: 0, y: 0, z: 0 } };

/** Scene units per kilometre: the world model is in metres. */
export const UNITS_PER_KM = 1000;

/**
 * The orrery's own sun, as three.js irradiance.
 *
 * Not a physical W/m2: one point light with no attenuation lights sixteen
 * bodies four decades of distance apart, so this is a look, and the rings read
 * it too (`src/atlas/rings.ts`). What it is NOT on is the scale the sky and the
 * aerial term are driven from, which is `solarFluxWm2 * SKY_EXPOSURE`; the
 * focused body's ground is converted onto that one in `atmoTerrainUniforms`
 * (src/atlas.ts) so ground and haze sum in one scale.
 */
export const ORRERY_SUN_INTENSITY = 2.4;

/**
 * The colour of the one sun, and of the fill that keeps a night side off black.
 *
 * Named because the walkaround's ground is lit by these too: below the SURFACE
 * edge the plot mesh takes over from the pyramid leaves, and a light of its own
 * there is a step in level and hue at the notch that crosses the edge
 * (`atlasGroundLight` in src/atlas.ts, `surface-clamp --edge`).
 */
export const ORRERY_SUN_COLOR = 0xfff2d8;
export const ORRERY_AMBIENT_COLOR = 0x556677;
export const ORRERY_AMBIENT_INTENSITY = 0.35;

/**
 * Floor on a body's drawn angular diameter, degrees.
 *
 * Three pixels at the reference viewport of `07-performance-and-instruments.md`
 * - 900 rendered rows over a 45 degree field - which is the smallest a body can
 * be drawn and still be seen and clicked. Held as an angle rather than a pixel
 * count so it does not move when the pane does, and so the two numbers in
 * `userData` mean the same thing at every viewport.
 */
export const MIN_DRAWN_ANGULAR_DEG = 0.15;

/**
 * How finely a round body's globe is tessellated.
 *
 * The limb is a polygon inscribed in the disc, so what a person sees at the
 * edge is the sagitta of one segment: `r * (1 - cos(pi / segments))` pixels of
 * the drawn radius. The silhouette of a lat/lon sphere is a meridian pair from
 * the side and the equator from over a pole, so the count that binds is the
 * smaller of `width` and `2 * height`. The widest framing that still holds a
 * whole body in the pane puts its drawn radius at about half the pane height,
 * 450 px at 900, and 96 keeps the sagitta there under a quarter of a pixel;
 * 32 by 24 left it at 1.9 px, which read as straight 40 px segments against
 * the sky over Uranus and Neptune. `descent-dojo --limb` is the criterion.
 * A body drawn as its own shape does not use this: `shapeGlobe.ts` builds its
 * own cubesphere.
 */
export const GLOBE_WIDTH_SEGMENTS = 96;
export const GLOBE_HEIGHT_SEGMENTS = 48;

/** Obliquity of the ecliptic at J2000, degrees (IAU 2006). */
const OBLIQUITY_DEG = 23.4392911;
const DEG = Math.PI / 180;

/**
 * Ecliptic J2000 (z toward the ecliptic pole) into scene axes (y up).
 *
 * The propagator and the star catalogue both work in the ecliptic frame; three
 * .js and every lat/lon in this repo are y-up. One rotation of -90 degrees
 * about x separates them, and this is the only place it is applied.
 */
export function eclipticToScene(v: Vec3, scale: number): WorldVec {
  return { x: v.x * scale, y: v.z * scale, z: -v.y * scale };
}

/** Equatorial ICRF unit vector into scene axes. */
function equatorialToScene(x: number, y: number, z: number, out: THREE.Vector3): THREE.Vector3 {
  const c = Math.cos(OBLIQUITY_DEG * DEG);
  const s = Math.sin(OBLIQUITY_DEG * DEG);
  const yEcl = y * c + z * s;
  const zEcl = -y * s + z * c;
  return out.set(x, zEcl, -yEcl);
}

const _bfAt: PoleAndMeridian = { raDeg: 0, decDeg: 0, wDeg: null };
const _bfPole = new THREE.Vector3();
const _bfPm = new THREE.Vector3();
const _bfZ0 = new THREE.Vector3();
const _bfBasis = new THREE.Matrix4();

/**
 * Body-fixed to scene rotation from the WGCCRE pole and prime meridian, periodic
 * terms included (`src/world/orientation.ts`).
 *
 * The one derivation of that rotation. `src/main.ts`'s land verb reads the
 * sub-ship lat/lon through it, so the point a ship lands on and the point a
 * click inside the Atlas resolves to come out of the same frame rather than
 * out of two that agree until a pole rate moves one of them.
 *
 * False where no pole is published: `out` is left alone and the caller is
 * looking at scene axes, which is what the globe draws on such a body too.
 */
export function bodyFixedQuaternion(
  row: BodyRow, jdTdb: number, out: THREE.Quaternion,
): boolean {
  if (!poleAndMeridianAt(row, jdTdb, _bfAt)) return false;
  const ra = _bfAt.raDeg * DEG;
  const dec = _bfAt.decDeg * DEG;
  equatorialToScene(
    Math.cos(dec) * Math.cos(ra), Math.cos(dec) * Math.sin(ra), Math.sin(dec), _bfPole,
  ).normalize();
  // The node of the body's equator on the ICRF equator, which is where W is
  // measured from; the prime meridian is W degrees east of it.
  equatorialToScene(-Math.sin(ra), Math.cos(ra), 0, _bfPm).normalize();
  // Bennu is the Phase 1 case: pole published, prime meridian and spin rate
  // not. Substituting W = 0 would draw a body that looks deliberately locked
  // to a meridian rather than simply unmodelled, so short of a real W the node
  // itself stands in as the body-fixed reference and the body does not spin.
  if (_bfAt.wDeg !== null) {
    const w = _bfAt.wDeg * DEG;
    _bfZ0.copy(_bfPole).cross(_bfPm).normalize();
    _bfPm.multiplyScalar(Math.cos(w)).addScaledVector(_bfZ0, Math.sin(w)).normalize();
  }
  // Columns: prime meridian, north, and their cross - the frame every lat/lon
  // in this repo is written in (x at lon 0, y north, lon east toward -z).
  _bfZ0.copy(_bfPm).cross(_bfPole).normalize();
  _bfBasis.makeBasis(_bfPm, _bfPole, _bfZ0);
  out.setFromRotationMatrix(_bfBasis);
  return true;
}

/** One drawn sunspot group's place on Sol's billboard, in the disc's radii. */
export interface SolRegionDisc {
  readonly id: string;
  readonly measured: boolean;
  readonly areaMillionths: number;
  readonly radiusDeg: number;
  readonly umbraRadiusDeg: number;
  /** Toward solar west on screen, and toward north; mu at or under 0 is behind. */
  readonly x: number;
  readonly y: number;
  readonly mu: number;
}

export interface OrreryBody {
  readonly row: BodyRow;
  readonly mesh: THREE.Mesh;
  /**
   * The radius the sphere is DRAWN at, metres.
   *
   * `row.radius_m` until a pyramid says otherwise: the tile meshes are built on
   * the manifest's `datum_radius_m`, which on Mars is 608 m over the registry's
   * mean radius, and a globe on the smaller sphere is a second surface under
   * the first (docs/ATLAS-TERRAIN.md, "One sphere").
   */
  drawRadiusM: number;
  /** Heliocentric position in scene axes, metres, at the last update. */
  readonly world: WorldVec;
  /** Body-fixed to scene rotation, identity where no pole is published. */
  readonly quaternion: THREE.Quaternion;
  /** Pole, prime meridian AND spin rate all published - a body missing any of the four does not spin. */
  readonly rotationKnown: boolean;
  /**
   * What this globe's unmeasured imagery draws, or null on a body with no map.
   *
   * Carried on the body rather than kept inside the material, because
   * `src/atlas.ts` re-injects the aerial-perspective chunk into the same
   * material and has to put this one back with it.
   */
  readonly coverage: CoverageFillRef | null;
  /** What this globe draws over ground nobody imaged, or null where it draws none. */
  readonly artificial: ArtificialFillRef | null;
  /**
   * What this globe shades ground no altimeter reached by, or null on a row
   * that publishes no relief.
   *
   * Carried on the body for the tile pyramid's sake: the streamed meshes below
   * the ORBIT edge shade by the same map through the same switch, and there is
   * one relief per body to hand them. Null on a `classes` row too, whose tiles
   * displace their own ground by the field that map was sampled from.
   */
  readonly relief: ArtificialReliefRef | null;
  /** The shadows its own system throws on this globe and its tiles, or null where nothing can (`bodyShadow.ts`). */
  readonly shadow: BodyShadowRef | null;
  /**
   * Titan's runtime-generated relief, null until both terrain-class maps land,
   * or on a body whose row carries no `classes` block.
   */
  tileRelief: TileReliefSampler | null;
  /** Bumped whenever `tileRelief` lands, so the tile band knows to remesh. */
  tileReliefVersion: number;
  /** Goes round another body rather than the Sun, which is what the layer rows split on. */
  readonly satellite: boolean;
  orbitLine: THREE.LineLoop | null;
}

/**
 * The view layers the player switches, ANDed with the band gates the ladder
 * already applies. A layer added later is one more member here and one more
 * row in the panel, not a new setter.
 */
export type OrreryLayer =
  'orbitsHelio' | 'orbitsMoons' | 'spirals' | 'labels' | 'moonLabels' | 'rings';

/**
 * One satellite's heliocentric trace, as offsets from a double anchor.
 *
 * The offsets are float32 and the anchor is not: an AU-scale vertex quantises
 * to about 9 km, and the stretch of the trace the eye is on when it is zoomed
 * in is the stretch next to the anchor, which is where the moon is now.
 */
interface SatelliteSpiral {
  readonly row: BodyRow;
  readonly line: THREE.Line;
  readonly geo: THREE.BufferGeometry;
  readonly verts: Float32Array;
  /** The heliocentric scene point the offsets are measured from. */
  readonly anchor: WorldVec;
  /** Half the window, days. */
  readonly halfWindowDays: number;
  /** The epoch the window is centred on; NaN before the first build. */
  builtJd: number;
  /** The row's elements changed under a window already traced from them. */
  elementsStale: boolean;
}

/** One orbit ring's rebuild state: what epoch its samples were last taken around. */
interface OrbitRingTrack {
  readonly row: BodyRow;
  readonly orbit: Orbit;
  readonly satellite: boolean;
  readonly period: number;
  readonly verts: Float32Array;
  readonly geo: THREE.BufferGeometry;
  /** The body this ring is drawn for, for its live position and its line. */
  readonly body: OrreryBody;
  /** `a * (1 - cos(pi / RING_SEGS))` in scene units: the run's own gate term. */
  readonly sagUnits: number;
  /** The ring's semi-major axis in scene units: the scale it is drawn at. */
  readonly scaleUnits: number;
  /** How far this ellipse's own secular rates carry it in a day, scene units. */
  readonly driftUnitsPerDay: number;
  /**
   * The point the vertices are written relative to: the body's own position at
   * the epoch the ring was last built at, in the frame the ring is sampled in.
   */
  readonly anchor: WorldVec;
  /** Scratch for a satellite ring's live registration point, to not allocate a frame. */
  readonly point: WorldVec;
  builtJd: number;
  /** The body's eccentric anomaly at that build, for how far it has slid since. */
  builtE: number;
  /**
   * The row's elements changed under a ring already built from them: an
   * osculating sidecar row whose sets arrive after the registry does
   * (`ensureOsculating` in `src/world/bodies.ts`). Deimos stands 7.8 px off a
   * ring sampled from its secular fit, so the whole turn is re-sampled on the
   * next frame rather than waiting for the stale-epoch term.
   */
  elementsStale: boolean;
}

export interface OrreryConfig {
  /** Bodies are registered here; Sol, the stars and the lights are not. */
  frame: WorldFrameHandle;
  /** The Atlas's own subtree under the shared root, not the whole scene. */
  scene: THREE.Object3D;
  rows: readonly BodyRow[];
  /** TDB Julian date, read every update: the one clock. */
  jdTdb: () => number;
  /** Game seconds that date moves a real second; 0 held, paused or previewed. */
  clockRate: () => number;
  /** A texture decode is a frame the governor must not read as GPU cost. */
  onLoadStart: () => void;
  onLoadEnd: () => void;
  onNeedsRender: () => void;
  /**
   * Upload a texture now rather than on the frame that first draws it.
   *
   * The map is decoded to an ImageBitmap off the main thread, so this is a
   * blit and its mip generation; on the frame it would land with a program
   * link it was a 50 ms stall.
   */
  uploadTexture: (texture: THREE.Texture) => void;
  /**
   * Link the programs one object needs, off the frame that would first draw it.
   *
   * A first draw BLOCKS on the link even where the driver links in parallel
   * (KHR_parallel_shader_compile), so this is called ahead of the draw rather
   * than awaited.
   */
  linkPrograms: (object: THREE.Object3D) => void;
  /**
   * Keep a body that is still drawn as its sphere or ellipsoid off screen this
   * frame, at the true angular diameter it would be drawn at
   * (`src/atlas/shapeGlobe.ts`'s `holding`). Absent, every body draws.
   */
  shapeHold?: (bodyId: string, trueAngularDiameterDeg: number) => boolean;
}

export interface OrreryHandle {
  bodies: OrreryBody[];
  byId(id: string): OrreryBody | undefined;
  sol: SolHandle;
  /** Switched off at the plot tiers, which carry their own sun in-shader. */
  lights: THREE.Light[];
  /** The TDB Julian date `propagate` solves the bodies at. */
  jdTdb(): number;
  /**
   * Every body's heliocentric position and rotation at the clock's instant.
   *
   * Separate from `update` because a camera anchored to a body-fixed lat/lon
   * has to be solved against THIS frame's position and rotation: propagating
   * afterwards leaves the camera a frame behind the ground it is looking at,
   * which is 24 km of Mars's own orbital motion at the 60:1 clock.
   */
  propagate(): void;
  /** Positions, rotations and visibility floors for this frame. */
  update(cameraWorld: WorldVec): void;
  /** The ring system of a body that has one, for the instruments. */
  ringsFor(id: string): RingsHandle | undefined;
  /** The moons' and planets' shadows on each other, for the instruments. */
  readonly shadows: BodyShadowsHandle;
  /**
   * The light rig at `scale` times the look it is built at: the sun, the
   * ambient fill and every ring's sun with it. src/atlas.ts sets the focused
   * body's seasonal flux over its mean here, the factor its plot's `uSunFlux`
   * carries, and 1 at the SYSTEM band and in cruise. `groundShare` is the part
   * of `scale` that is `sunCover`'s ground share, at `groundWeight`; the body
   * shadows put it back where they take their own.
   */
  setSunScale(scale: number, groundShare?: number, groundWeight?: number): void;
  /**
   * The sun's light in the hue the air under the eye leaves of it, per channel,
   * or null for its own. Only the ground view passes one: from space the Sun is white.
   */
  setSunTint(tint: THREE.Vector3 | null): void;
  /** `descent-dojo --rings`: one tau across every annulus, or back to the profile. */
  setRingDebug(flatTau: number | null): void;
  /** `descent-dojo --rings --inject point-sample`: the three-tap read of the
   *  profile back, in place of the footprint mean. */
  setRingPointSample(on: boolean): void;
  /** `descent-dojo --rings --inject ice-albedo`: one particle albedo across
   *  every annulus, or back to each row's own. */
  setRingAlbedo(albedo: number | null): void;
  /** `descent-dojo --rings --inject gain-saturn`: one brightness factor across
   *  every annulus, or back to each row's own. */
  setRingGain(factor: number | null): void;
  /** `descent-dojo --rings --rings-body jupiter`: every annulus drawn empty,
   *  so the same pose can be read with and without the ring light. */
  setRingSuppressed(on: boolean): void;
  /** `descent-dojo --rings --inject ring-bright`: one component's own depth
   *  multiplied, on every annulus that has that many components. */
  setRingComponentScale(index: number | null, factor: number): void;
  /** Orbit rings are SYSTEM decoration and quantise badly close up. */
  setRingsVisible(on: boolean): void;
  /**
   * `orbit-rings --rings-epoch`: what the ring rebuild pass has cost since the
   * last read, and how much of it the zoom gate let through. Reading resets it,
   * so a caller measures a window rather than a total.
   */
  readRingRebuild(): {
    frames: number; ms: number; rebuilds: number; verts: number; rings: number;
    /** Vertices the per-frame run re-placed, as against the `verts` re-sampled. */
    tracked: number;
  };
  /**
   * A row's own elements changed under the ring already drawn from them, so
   * re-sample the whole turn on the next frame.
   *
   * The osculating sidecar is fetched per body and a satellite's sets arrive
   * after the registry does (`ensureOsculating` in `src/world/bodies.ts`); the
   * body is placed from the live elements on every frame but its ring is not,
   * and a ring left on the secular fit stands deimos 7.8 px off it.
   */
  refreshElements(bodyId: string): void;
  /** Draw a body's sphere at another radius - the pyramid's datum. */
  setDrawRadius(bodyId: string, radiusM: number): void;
  /** Back to the radius the body's own geometry is built on once no pyramid
   *  holds it: its shape's datum, or the registry radius for a sphere. */
  resetDrawRadius(bodyId: string): void;
  /**
   * Draw a body as its own shape rather than as a sphere, or put the sphere
   * back.
   *
   * The geometry belongs to whoever built it (`src/atlas/shapeGlobe.ts`) and is
   * never disposed here; the sphere it replaces is, with the orrery.
   * `datumRadiusM` is the radius the shape's relief is measured from, and the
   * globe is drawn at it from then on. It is the pyramid's datum, 608 m over
   * the registry radius on Mars: drawn on the registry radius, the globe grew
   * that much under a low ship the moment the pyramid opened.
   */
  setShapeGeometry(
    bodyId: string, geometry: THREE.BufferGeometry | null, datumRadiusM?: number,
  ): void;
  /** The pyramid's datum known before the shape is: the sphere is drawn at it
   *  from here on, as the shape will be. */
  setDatumRadius(bodyId: string, datumRadiusM: number): void;
  /**
   * Rebuild one body's sphere at another tessellation.
   *
   * `descent-dojo --limb --inject coarse-globe` alone: the shipped count is
   * `GLOBE_WIDTH_SEGMENTS` / `GLOBE_HEIGHT_SEGMENTS` and nothing else moves it.
   */
  setGlobeSegments(bodyId: string, widthSegments: number, heightSegments: number): void;
  /**
   * The shape controls alone: a body not yet on its shape draws as the sphere
   * rather than the registry ellipsoid it otherwise stands in with.
   */
  setRoundGlobes(on: boolean): void;
  /**
   * What unmeasured imagery draws on every globe, or null for the fill each
   * map already carries.
   *
   * `descent-dojo --inject nodata-black` and `--inject fill-row` are the only
   * callers: the substitution is the identity in normal use
   * (`coverageFill.ts`), and forcing another colour through it is what proves
   * the coverage the maps carry is real.
   */
  setUnmeasuredFill(cssColor: string | null): void;
  /**
   * Record what one body's globe map actually filled its holes with, off the
   * derived colour its pyramid just published (`pyramid.json`'s
   * `roles.albedo.unmeasured_rgb`), so a reader asking what the frame draws
   * there is answered with the map's own fill rather than the registry's
   * `albedo_color` stand-in. Reporting only: the frame draws the texel until a
   * control substitutes for it. A no-op on a body with no textured globe (no
   * `texture` row at all).
   */
  setCoverageFill(bodyId: string, cssColor: string, transfer?: AlbedoTransfer | null): void;
  /**
   * Draw artificial imagery over the ground nobody imaged, or do not.
   *
   * Off is a bit-identical frame: the composite is a mix weighted by the
   * artificial map's own alpha times this switch, so at 0 every texel is the
   * one the body already drew. A body whose registry row publishes no
   * `artificial` block is unaffected either way.
   */
  setArtificialFill(on: boolean): void;
  /** Fetch the few-KB sunspot model, once, when a player opens Sol. */
  loadSolPhotosphere(): void;
  /** The LIVE SUN row: the live photosphere, or the 304 A map as it was. */
  setSolLive(on: boolean): void;
  /** `--live-sun`'s controls: hold the date, draw no region, fetch the record
   *  eagerly, or put the live layer back on the bare disc; `rate` and
   *  `strobe` are the granule turnover's own (`SolPhotosphereHandle.setDebug`). */
  setSolPhotosphereDebug(d: {
    frozen?: boolean; noSpots?: boolean; eagerRecord?: boolean; whiteDisc?: boolean;
    rate?: number | null; strobe?: boolean;
  }): void;
  /** What the disc's live layer holds this frame, for the honesty card. */
  solPhotosphereReading(): SolPhotosphereReading;
  /** Where each drawn region sits on the billboard, in the disc's own radii. */
  solRegionDisc(): readonly SolRegionDisc[];
  /**
   * Whether this body's globe is drawing artificial imagery right now, which
   * needs the layer on AND the map landed. False on a body that has none, so a
   * panel reports what the frame does rather than what the switch says.
   */
  artificialDrawn(bodyId: string): boolean;
  /**
   * Whether this body's globe is shading by artificial relief right now, which
   * needs the same layer on AND its own slope map landed. A separate reading
   * from the imagery above: a body can carry one and not the other.
   */
  artificialReliefDrawn(bodyId: string): boolean;
  /** How many globes are compositing it right now, and how many could. */
  artificialCount(): { drawn: number; total: number };
  /** `descent-dojo --artificial`'s two controls: paint over measured ground,
   *  or composite nothing at all. */
  setArtificialDebug(next: {
    overMeasured?: boolean; inert?: boolean; reliefInert?: boolean; reliefUnconditioned?: boolean;
    reliefRepeat?: number;
  }): void;
  /**
   * Draw the body names, or do not.
   *
   * Off below ORBIT, where the focused body IS the frame and every other row
   * is somewhere behind the camera.
   */
  setNamesVisible(on: boolean): void;
  /**
   * One view layer on or off, on top of whatever the band gates say. The
   * ladder keeps driving `setNamesVisible` and `setRingsVisible` per frame;
   * these are ANDed with those rather than fighting them.
   */
  setLayerVisible(layer: OrreryLayer, on: boolean): void;
  /** What the panel's own row reads back. */
  layerVisible(layer: OrreryLayer): boolean;
  /**
   * What each layer DREW last frame against what it has to draw, off the
   * scene graph rather than off the switch, so an instrument reading this is
   * reading the frame and not the preference behind it.
   */
  layerCounts(): Record<OrreryLayer, { drawn: number; total: number }>;
  /**
   * Place every drawn name in screen space and drop the ones that collide.
   *
   * Its own step rather than part of `update`, because the boxes are pixels
   * and `update` is handed nothing but the camera's heliocentric position.
   * Call it once per tick after `update`, with the pane's CSS size.
   */
  layoutLabels(camera: THREE.Camera, viewportH: number): void;
  /** Every body's name as it was drawn last frame. */
  labelState(): readonly LabelState[];
  /**
   * The body whose drawn name covers this point of the frame, NDC, or null.
   *
   * Out here a body is drawn at the angular floor - a dot about two pixels
   * across - with its name standing clear above it, so the name is the only
   * target the eye has to aim at. Not handed up to a primary: a name is drawn
   * only once the declutter has cleared it of its primary's, so a drawn name
   * addresses one body.
   */
  pickLabelAtNdc(x: number, y: number): string | null;
  /** `descent-dojo --labels`: the two controls, and the paint-diff switch. */
  setLabelDebug(next: {
    suppress?: boolean; noDeclutter?: boolean; surfaceGate?: boolean;
  }): void;
  dispose(): void;
}

/**
 * Whether the orrery's own ink is drawn through the tone mapper.
 *
 * Ink is drawn, not lit. A line and a name say the same thing whatever the
 * globe beside them is metered to, and at a body's own band that metering is
 * Venus's 0.09, which put every one of them through ACES at a thirteenth of a
 * stop and left the frame dark red. Off, the ink bypasses exposure and tone
 * mapping together, so each colour constant below IS the pixel it lands on -
 * which is why they read as the tone-mapped values they replace and not as
 * the raw ones. `descent-dojo --layers --inject ink-tonemapped` puts it back.
 */
export const inkToneMapped = devFlag(() => TEST_SWITCHES && urlFlag('inktonemapped'));

/**
 * The navigable orange: a heliocentric orbit ring, and a landable body's name.
 *
 * 0xff8855 as it drew at the SYSTEM band, exposure 1.25, through ACES: the
 * look is the one that shipped, carried across to a constant the frame now
 * takes literally. Every ink colour under this one is the same substitution.
 */
const NAVIGABLE_COLOR = 0xf4af76;
const NAVIGABLE_CSS = `#${NAVIGABLE_COLOR.toString(16).padStart(6, '0')}`;

/**
 * The name's cap height, as an angle.
 *
 * An angle rather than a pixel count, for the same reason as
 * `MIN_DRAWN_ANGULAR_DEG`: it means the same thing at every viewport. On the
 * reference pane - 687 rendered rows over a 45 degree field - the name is
 * 14 pixels tall, standing 18.6 px over a 2-pixel dot.
 */
const NAME_HEIGHT_DEG = 0.92;

/**
 * Where a name stops being a finding aid, as the body's drawn half-angle.
 *
 * A name exists to say which dot is which, so once the disc is large enough to
 * read as a body it has already answered that and the name goes - faded out
 * over the octave below the edge rather than switched, so a wheel through it
 * does not pop.
 */
const NAME_FADE_FULL_HALF_DEG = 0.25;
export const NAME_FADE_GONE_HALF_DEG = 0.5;

/** A body nobody can land on is named in plain grey: the orange means navigable. */
const LABEL_PLAIN_CSS = '#d5dade';

/**
 * How loud an orbit ring is drawn.
 *
 * Hierarchy, not landability: a ring says what a body goes round, and the
 * name's own colour is what says whether there is ground to land on. A
 * satellite's is dim because it is drawn inside its primary's own ring at a
 * fraction of its radius, and at the framing that holds the whole system a
 * loud one would read as clutter around a dot.
 */
const HELIO_ORBIT_OPACITY = 0.55;
const SATELLITE_ORBIT_COLOR = 0x2a5e76;
const SATELLITE_ORBIT_OPACITY = 0.28;
const SPIRAL_COLOR = 0x6ea6c3;
const SPIRAL_OPACITY = 0.34;

/**
 * How far either side of now a satellite's spiral runs, in the satellite's OWN
 * turns.
 *
 * A count of the moon's turns rather than a share of the parent's period: it
 * draws the same picture for every satellite and keeps each spiral legible at
 * the zoom that moon's ring was legible at, where one parent period is Luna's
 * whole scalloped near-circle and 2,290 coils of Phobos. Six turns also leaves
 * the two ends more than a turn apart on every row, so both read as ends; at
 * the 13.4 turns that would close Luna's they meet and it reads as a ring.
 */
const SPIRAL_TURNS_EITHER_SIDE = 3;
/** Samples per turn. */
const SPIRAL_SAMPLES_PER_TURN = 256;
/**
 * Re-sample once the clock has carried now this far off the window's centre,
 * as a share of the half window: 0.24 days of Phobos, 16 days of Luna.
 */
const SPIRAL_RECENTRE_FRACTION = 0.2;
/** `datum-jump-check.mjs --inject registry-draw`: a shape globe drawn on the
 *  registry radius until a pyramid opens, the reading before it took its datum. */
const registryDraw = devFlag(() => TEST_SWITCHES && urlFlag('registrydraw'));
/**
 * `orbit-rings --spiral --inject spiral-pinned`: the window built once at the
 * epoch it was first drawn at and never re-centred. Read here rather than in
 * `src/atlas.ts` so the switch sits beside the code it disables, the way
 * `src/atlas/rover.ts` reads its own probe flag.
 */
const spiralPinned = devFlag(() => TEST_SWITCHES && urlFlag('spiralpinned'));
/**
 * `orbit-rings --spiral --inject spiral-needs-orbits`: restores the dropped
 * `layerOn.orbitsMoons` term below, the control for MOON ORBITS gating the
 * ring alone and never the spiral.
 */
const spiralNeedsOrbits = devFlag(() => TEST_SWITCHES && urlFlag('spiralneedsorbits'));
/**
 * `descent-dojo --layers --inject spiral-stuck-on`: the layers arm's control on
 * the spirals row. The spiral is drawn wherever it COULD be drawn and its own
 * switch reaches it no more; nothing a player reaches sets it.
 */
const spiralStuckOn = devFlag(() => TEST_SWITCHES && urlFlag('spiralstuckon'));

/**
 * Points per ring, one turn of eccentric anomaly each.
 *
 * The ring is a polyline, so between two vertices it cuts INSIDE the ellipse by
 * the chord sag `a * (1 - cos(pi / RING_SEGS))`, and a body sitting in the
 * middle of a chord is that far off its own line: 17,000 km at Mars at 256.
 * Vertex 0 is the body itself, so what is left is how far the chord beside it
 * departs from the curve out at the edge of the pane, which `orbit-rings
 * --rings-epoch` reads and which at 768 is a hundredth of a pixel.
 */
const RING_SEGS = 768;
/**
 * How much of the E spacing is moved toward the body, 0 for none.
 *
 * `E_i = E_body + 2*pi*u - k*sin(2*pi*u)`: the step either side of the body is
 * `(1 - k)` of an even one and the step on the far side `(1 + k)`, and a
 * chord's departure from the curve goes as the square of its own step. The far
 * side is two semi-major axes further from a camera parked at the body, so the
 * same kilometres there cover far fewer pixels.
 */
const RING_E_WARP = 0.5;
/**
 * Radians one pixel covers at the reference viewport of
 * `07-performance-and-instruments.md`: 900 rendered rows over a 45 degree
 * field, which is the viewport `MIN_DRAWN_ANGULAR_DEG` and
 * `GLOBE_WIDTH_SEGMENTS` are reasoned in, so a pixel bar here does not move
 * when the pane does.
 */
const REF_PIXEL_RAD = (45 / 900) * DEG;
/**
 * Re-place the run of vertices beside the body once its own chord sag covers
 * this much of a pixel at the range the BODY is drawn at.
 *
 * Read this way the term is a question about the body, not about the ring: it
 * asks whether the polyline still passes through the dot, and it turns true as
 * soon as the camera comes within `(1 - cos(pi / RING_SEGS)) / RING_GATE_PX`
 * of a reference pixel - 3.8 percent - of the ring's own semi-major axis. A
 * click in the body list parks it at a hundred-thousandth of that, so at focus
 * zoom the term is not near the bar, it is hundreds to tens of thousands of
 * times over it, and the answer every frame is yes. What that used to buy was
 * a re-sample of the whole turn to move one vertex; now it buys the run.
 */
const RING_GATE_PX = 0.25;
/**
 * How far a ring's own ellipse may have precessed out from under it before the
 * whole turn is re-sampled, in pixels at the ring's own scale.
 *
 * The node and perihelion rates turn the whole ellipse, and at Phobos they
 * carry it 72 km a day, which is two thirds of a pixel at the framing where
 * Phobos separates from Mars. The scale is floored at the semi-major axis
 * because that is the distance the ring is drawn at: a camera sitting ON the
 * body is a few thousand km from one vertex and an orbit's width from all the
 * rest, and sizing the turn at the near vertex's pixel read every ring as
 * hundreds of pixels adrift a frame.
 */
const RING_DRIFT_PX = 0.1;
/**
 * How many vertices either side of the body the run re-places each frame.
 *
 * One step already spans tens of thousands of pixels at the zoom the gate
 * fires at, so the run only has to reach past the pane; what it buys over a
 * whole re-sample is 768 vertices of arithmetic and of buffer upload a frame.
 */
const RING_LOCAL_SEGS = 2;
/**
 * How much of one near-body step the body may slide past the vertex it was
 * built at before the whole turn is re-sampled anyway.
 *
 * The run is written at the body's live E onto the grid the rest of the ring
 * still holds, so the step into the first stale vertex ahead shrinks by this
 * much while the one behind grows by it. At three quarters the leading step is
 * still a quarter of one, so the polyline cannot double back on itself, and
 * the trailing chord's sag is under a fortieth of a pixel at the ring's scale.
 */
const RING_LOCAL_SLIDE = 0.75;
/** Radians of E between the body and the vertex beside it, the warp applied. */
const RING_STEP_NEAR = ((2 * Math.PI) / RING_SEGS) * (1 - RING_E_WARP);
/**
 * A gated ring is rebuilt anyway once the clock has carried this many days
 * past the epoch it was built at. The drift term above cannot see an
 * osculating row step to the next yearly set, which moves the ellipse with no
 * rate behind it; this catches that within a day.
 */
const RING_STALE_DAYS = 1;
/**
 * `orbit-rings --inject rings-frozen-epoch`: every ring built once at its own
 * row's `epoch_jd`, in even steps of TIME, and never rebuilt - what shipped
 * before the ring was phase-locked, kept as the control for it.
 */
const ringsFrozenEpoch = devFlag(() => TEST_SWITCHES && urlFlag('ringsfrozenepoch'));
/**
 * `orbit-rings --inject rings-uniform-phase`: E swept from zero rather than
 * from the body's own E, and the run beside the body off with it. The body is
 * then between vertices again and rides the chord sag in and out, which is the
 * fault the phase lock exists to remove.
 */
const ringsUniformPhase = devFlag(() => TEST_SWITCHES && urlFlag('ringsuniformphase'));
/**
 * `orbit-rings --rings-jitter --inject ring-sun-frame`: ring vertices written
 * as absolute heliocentric positions again, the way the phase-locked ring
 * shipped. float32 quantises those to a step of `2^-23` of the Sun-to-body
 * distance - 33 km out at Ceres, 520 km at Pluto - and a ring rebuilt every
 * frame lands on a different step each time, which is the vibration.
 */
const ringSunFrame = devFlag(() => TEST_SWITCHES && urlFlag('ringsunframe'));
/**
 * `orbit-rings --rings-jitter --inject rings-rebuild-every-frame`: the gate as
 * it shipped - no run, and both terms sized at the pixel the BODY is drawn at,
 * where a focused body's own chord sag is hundreds of pixels and the whole turn
 * is re-sampled every frame. The control for the rate.
 */
const ringRebuildEveryFrame = devFlag(() => TEST_SWITCHES && urlFlag('ringsrebuildeveryframe'));
/**
 * `orbit-rings --rings-jitter --inject rings-no-local-track`: the run off,
 * leaving the whole-ring terms alone to hold the line on the body - the
 * control for the offset, and the reading of what the gate was buying.
 */
const ringNoLocalTrack = devFlag(() => TEST_SWITCHES && urlFlag('ringsnolocaltrack'));
/**
 * The waits before a body map that failed to load is asked for again. One
 * dropped request used to leave that globe its flat albedo colour until the
 * page was reloaded, since nothing else ever asks for the map.
 */
const MAP_RETRY_MS = [1_000, 3_000, 10_000];
/** `globe-imagery-check.mjs --inject no-retry`: one attempt per map, the reading before retries. */
const globeMapNoRetry = devFlag(() => TEST_SWITCHES && urlFlag('globemapnoretry'));
/** `globe-imagery-check.mjs --inject no-cancel`: leave a pending retry running past dispose. */
const globeMapNoCancel = devFlag(() => TEST_SWITCHES && urlFlag('globemapnocancel'));

/**
 * The clear space a name needs around another name, as a share of its own
 * height, and the band it comes up over.
 *
 * Two names closer than half a cap height read as one word - Pluto and Charon
 * at the system view were a single smear - so a label whose box comes within
 * half a height of a higher-priority one is not drawn at all, and it ramps to
 * full over the next half height rather than switching, so a camera drifting
 * across the threshold fades instead of popping.
 */
export const LABEL_CLEAR_MARGIN = 0.5;
export const LABEL_CLEAR_RAMP = 0.5;

/**
 * How opaque a name has to be drawn before a click on it means anything.
 *
 * A name coming up out of a collision is on its way to legible and is not yet
 * something a player would aim at, and two names still inside each other's
 * ramp would hand the same pixel two answers.
 */
export const LABEL_PICK_MIN_OPACITY = 0.5;

/** One body's name as it was drawn, for the instruments. */
export interface LabelState {
  readonly id: string;
  readonly text: string;
  readonly visible: boolean;
  readonly opacity: number;
  /** The drawn box in CSS pixels, y down from the top of the pane. */
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  /** The same box in NDC, y UP: what a framebuffer read addresses it with. */
  readonly nl: number;
  readonly nr: number;
  readonly nt: number;
  readonly nb: number;
  /** Pixels of clear space to the nearest higher-priority box; -1 with none. */
  readonly clearPx: number;
  /** The row's own fade from its disc growing past a dot, before the declutter. */
  readonly discFade: number;
  /** Whether a box was solved for it THIS frame: a row with none was never
   *  offered to the declutter at all. */
  readonly boxed: boolean;
  /** The row this one is a satellite of, or null. */
  readonly parent: string | null;
}

interface BodyLabel {
  readonly id: string;
  readonly sprite: THREE.Sprite;
  readonly mat: THREE.SpriteMaterial;
  readonly text: string;
  readonly parent: string | null;
  /** This frame's reading, written by `update` then by `layoutLabels`. */
  discFade: number;
  gated: boolean;
  trueHalfDeg: number;
  sortKey: number;
  left: number;
  top: number;
  right: number;
  bottom: number;
  nl: number;
  nr: number;
  nt: number;
  nb: number;
  clearPx: number;
  opacity: number;
  boxed: boolean;
}

/** The body's name as a texture: one canvas, drawn once, never re-measured. */
function bodyNameTexture(
  text: string, cssColor: string,
): { texture: THREE.CanvasTexture; aspect: number } | null {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const font = 'bold 40px ui-monospace, SFMono-Regular, Menlo, monospace';
  ctx.font = font;
  canvas.width = Math.ceil(ctx.measureText(text).width) + 24;
  canvas.height = 56;
  // Sizing the canvas resets the context, so everything below it is set again.
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 6;
  ctx.strokeStyle = 'rgba(4,7,12,0.85)';
  ctx.strokeText(text, canvas.width * 0.5, canvas.height * 0.5);
  ctx.fillStyle = cssColor;
  ctx.fillText(text, canvas.width * 0.5, canvas.height * 0.5);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  // Screen-sized already, so a mip chain would only blur it.
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;
  return { texture, aspect: canvas.width / canvas.height };
}

/** A CSS hex as one texel: the stand-in a globe wears until its map arrives. */
function flatTexel(cssColor: string): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = cssColor;
    ctx.fillRect(0, 0, 1, 1);
  }
  return canvas;
}

/** Sidereal period in days from the element set's own mean-longitude rate. */
function periodDays(row: BodyRow): number {
  const rate = row.orbit?.rates.L_deg_cy ?? 0;
  if (!row.orbit || Math.abs(rate) < 1e-9) return 0;
  return Math.abs((360 * DAYS_PER_CENTURY) / rate);
}

/**
 * Km a day the whole ellipse moves under its own secular rates - node,
 * perihelion and inclination summed, since they act on different axes and a
 * sum is the only bound that holds whatever the geometry. Mean anomaly is left
 * out: it moves the body ALONG the ellipse and leaves the ring's own points
 * where they were. A lunar theory's ellipse is the osculating one through its
 * state, which moves far faster than its mean rates turn it, so it has its own.
 */
function ellipseDriftKmPerDay(orbit: Orbit): number {
  if (orbit.theory === 'meeus_ch47') return LUNAR_ELLIPSE_DRIFT_KM_PER_DAY;
  const r = orbit.rates;
  return orbit.a_au * AU_KM * (Math.abs(r.long_peri_deg_cy) + Math.abs(r.long_node_deg_cy)
    + Math.abs(r.i_deg_cy)) * DEG / DAYS_PER_CENTURY;
}

export function createOrrery(config: OrreryConfig): OrreryHandle {
  const { frame, scene, rows } = config;
  const teardown: Array<() => void> = [];
  const own = (fn: () => void): void => { teardown.push(fn); };

  // Sol is the one row the globe loop below skips - its orbit is null, it is
  // the frame's own origin - so its map, its coverage fill and its rotation
  // are wired here instead of there.
  const solRow = rows.find((r) => r.id === 'sol') ?? null;
  // The candidate maps a bake writes under public/art/atlas/planets/sol-candidates/
  // are git-ignored and never reach a build, so this is dev only: it exists so
  // one look can be held against another on the running page rather than by
  // rebaking and reloading.
  const solMapOverride = (committed: string | null): string | null => {
    if (!TEST_SWITCHES) return committed;
    const name = new URLSearchParams(location.search).get('solmap');
    if (!name || !/^[a-z0-9-]+$/.test(name)) return committed;
    return `/art/atlas/planets/sol-candidates/${name}.webp`;
  };
  const solCover = createCoverageFill(
    solRow?.bake?.global.find((g) => g.role === 'albedo')?.unmeasured_rgb
    ?? solRow?.albedo_color ?? '#ffffff');
  // Built here because the disc's uniforms are built with its material, and
  // registered below with every other body's once `arts` exists.
  const solFillRow = solRow?.artificial ? solRow : null;
  let solArt: ArtificialFillHandle | null = null;
  if (solFillRow !== null) {
    solArt = createArtificialFill(false, () => { loadArtificialMap(solArt!, solFillRow); });
  }
  const solPhoto = createSolPhotosphere();
  own(() => solPhoto.dispose());
  const sol = createSol({
    unitsPerKm: UNITS_PER_KM,
    minAngularDiameterDeg: MIN_DRAWN_ANGULAR_DEG,
    texture: solMapOverride(solRow?.texture ?? null),
    albedoColor: solRow?.albedo_color ?? '#ffffff',
    coverage: solCover.ref,
    artificial: solArt?.ref ?? null,
    photosphere: solPhoto.ref,
  });
  scene.add(sol.mesh);
  own(() => sol.dispose());
  // The record's longitudes are Carrington's and the disc's shader works in the
  // body-fixed frame, so the offset between them is taken from Earth's own
  // position here rather than assumed from the two definitions.
  let solFrozen = false;
  const _subEarth = new THREE.Vector3();
  const _solQInv = new THREE.Quaternion();
  /**
   * Where the sub-Earth point sits in Sol's body-fixed frame, from Earth's
   * own heliocentric position - taken from the body loop's own solve rather
   * than a second one, since `propagate()` already computes it there.
   */
  function subEarthBodyLonRad(earthHelioPos: Vec3 | null): number | null {
    if (earthHelioPos === null || solRow === null) return null;
    const s = eclipticToScene(earthHelioPos, 1);
    _subEarth.set(s.x, s.y, s.z).normalize()
      .applyQuaternion(_solQInv.copy(_solQ).invert());
    return Math.atan2(-_subEarth.z, _subEarth.x);
  }

  // One point light AT the Sun with no attenuation, rather than a directional
  // light with a hand-picked direction: every body is then lit from the true
  // direction to the Sun, which is the only way sixteen of them can be lit at
  // once. Ambient keeps the night side off pure black.
  const sunLight = new THREE.PointLight(ORRERY_SUN_COLOR, ORRERY_SUN_INTENSITY, 0, 0);
  const ambient = new THREE.AmbientLight(ORRERY_AMBIENT_COLOR, ORRERY_AMBIENT_INTENSITY);
  scene.add(sunLight);
  scene.add(ambient);
  // This point light is the LAST one the scene gets: the registry's fetch
  // decides when the orrery mounts, so it lands after the sim's own rig and
  // after a fight's light pool. Everything keyed on the count that reads no
  // light is rekeyed here, or it holds a program one light short.
  relinkLightKeyed();
  const lights: THREE.Light[] = [sunLight, ambient];
  const _sunTinted = new THREE.Color();
  const _sunTintColor = new THREE.Color();
  own(() => {
    for (const l of lights) { scene.remove(l); l.dispose(); }
    relinkLightKeyed();
  });

  const ORIGIN: WorldVec = { x: 0, y: 0, z: 0 };
  /**
   * Re-sample one satellite's trace over a window centred on `jd`.
   *
   * `heliocentric` rather than `stateAt`, which is the whole difference between
   * this and the ring: the same elements and the same solver the bodies
   * themselves are placed with, composed onto the parent, so the moon is on the
   * line by construction rather than by agreement.
   */
  function buildSpiral(sp: SatelliteSpiral, jd: number): void {
    const centre = eclipticToScene(heliocentric(sp.row, rows, jd).position, UNITS_PER_KM);
    sp.anchor.x = centre.x;
    sp.anchor.y = centre.y;
    sp.anchor.z = centre.z;
    const n = sp.verts.length / 3;
    for (let i = 0; i < n; i++) {
      const t = jd + ((2 * i) / (n - 1) - 1) * sp.halfWindowDays;
      const p = eclipticToScene(heliocentric(sp.row, rows, t).position, UNITS_PER_KM);
      sp.verts[i * 3] = p.x - centre.x;
      sp.verts[i * 3 + 1] = p.y - centre.y;
      sp.verts[i * 3 + 2] = p.z - centre.z;
    }
    (sp.geo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    sp.builtJd = jd;
    sp.elementsStale = false;
  }
  /**
   * (Re)sample one ring in ECCENTRIC ANOMALY, phase-locked to its own body.
   *
   * Vertex 0 is `E_body` at `jd`, out of the same `elementsAt` the body was
   * placed from, so the body is not between two vertices - it IS one, to the
   * bit, and the chord sag it used to ride in and out of cannot reach it. The
   * rest of the turn is analytic from that one element set: two trig calls and
   * a rotation a vertex, no Kepler solve.
   *
   * A satellite is sampled in its PARENT's frame, which is the frame its
   * elements are in and the frame its line is registered in: its parent moves
   * along its own orbit while the satellite goes round once, so heliocentric
   * samples over a satellite period trace an open spiral, not this ring.
   *
   * The vertices are written relative to the BODY's own position at `jd`, and
   * the line is registered at that point, for the reason `world/frame.ts`
   * writes every other node camera-relative: a float32 attribute holding an
   * absolute heliocentric metre quantises to `2^-23` of it, which is 33 km at
   * Ceres and 520 km at Pluto, and a ring rebuilt every frame lands on a
   * different step each time.
   */
  function buildRing(track: OrbitRingTrack, jd: number): void {
    const { row, orbit, satellite, period, verts, anchor } = track;
    const sunFrame = ringSunFrame();
    if (ringsFrozenEpoch()) {
      const first = satellite ? stateAt(orbit, jd) : heliocentric(row, rows, jd);
      const c = eclipticToScene(first.position, UNITS_PER_KM);
      anchor.x = sunFrame ? 0 : c.x;
      anchor.y = sunFrame ? 0 : c.y;
      anchor.z = sunFrame ? 0 : c.z;
      for (let i = 0; i < RING_SEGS; i++) {
        const t = jd + (i / RING_SEGS) * period;
        const state = satellite ? stateAt(orbit, t) : heliocentric(row, rows, t);
        const p = eclipticToScene(state.position, UNITS_PER_KM);
        verts[i * 3] = p.x - anchor.x;
        verts[i * 3 + 1] = p.y - anchor.y;
        verts[i * 3 + 2] = p.z - anchor.z;
      }
    } else {
      const el = elementsAt(orbit, jd);
      const c = eclipticToScene(positionFromE(el, el.E), UNITS_PER_KM);
      anchor.x = sunFrame ? 0 : c.x;
      anchor.y = sunFrame ? 0 : c.y;
      anchor.z = sunFrame ? 0 : c.z;
      track.builtE = el.E;
      const base = ringsUniformPhase() ? 0 : el.E;
      for (let i = 0; i < RING_SEGS; i++) {
        const u = (i / RING_SEGS) * (2 * Math.PI);
        const p = eclipticToScene(
          positionFromE(el, base + u - RING_E_WARP * Math.sin(u)), UNITS_PER_KM,
        );
        verts[i * 3] = p.x - anchor.x;
        verts[i * 3 + 1] = p.y - anchor.y;
        verts[i * 3 + 2] = p.z - anchor.z;
      }
    }
    const pos = track.geo.getAttribute('position') as THREE.BufferAttribute;
    // A run may have left a partial upload range behind; the whole buffer goes.
    pos.clearUpdateRanges();
    pos.needsUpdate = true;
    track.builtJd = jd;
    track.elementsStale = false;
    ringVertsBuilt += RING_SEGS;
  }
  /**
   * Re-place the short run of vertices beside the body so the drawn line still
   * passes through it, without re-sampling the whole turn.
   *
   * The run is written at the body's LIVE eccentric anomaly on the same warped
   * grid `buildRing` laid down, so it meets the stale vertices either side at
   * a step that has shrunk on the leading side and grown on the trailing one.
   * False once that shrink has eaten `RING_LOCAL_SLIDE` of a step: past there
   * the run would walk into its own neighbour, and the caller re-samples.
   */
  function trackRing(track: OrbitRingTrack, jd: number): boolean {
    const { orbit, verts, anchor } = track;
    const el = elementsAt(orbit, jd);
    const turn = 2 * Math.PI;
    let slid = el.E - track.builtE;
    slid -= Math.round(slid / turn) * turn;
    if (!(Math.abs(slid) <= RING_LOCAL_SLIDE * RING_STEP_NEAR)) return false;
    for (let i = -RING_LOCAL_SEGS; i <= RING_LOCAL_SEGS; i++) {
      const u = (i / RING_SEGS) * turn;
      const p = eclipticToScene(
        positionFromE(el, el.E + u - RING_E_WARP * Math.sin(u)), UNITS_PER_KM,
      );
      const k = (i + RING_SEGS) % RING_SEGS;
      verts[k * 3] = p.x - anchor.x;
      verts[k * 3 + 1] = p.y - anchor.y;
      verts[k * 3 + 2] = p.z - anchor.z;
    }
    const pos = track.geo.getAttribute('position') as THREE.BufferAttribute;
    // Two ranges rather than the whole attribute: the run wraps past vertex 0.
    pos.addUpdateRange(0, (RING_LOCAL_SEGS + 1) * 3);
    pos.addUpdateRange((RING_SEGS - RING_LOCAL_SEGS) * 3, RING_LOCAL_SEGS * 3);
    pos.needsUpdate = true;
    ringVertsTracked += 2 * RING_LOCAL_SEGS + 1;
    return true;
  }
  const bodies: OrreryBody[] = [];
  const byId = new Map<string, OrreryBody>();
  const shadows = createBodyShadows(rows);
  own(() => shadows.dispose());
  const minHalfAngle = MIN_DRAWN_ANGULAR_DEG * 0.5 * DEG;
  /** By body id, for every row with an orbit: a name does not need a surface. */
  const labels = new Map<string, BodyLabel>();
  /** By body id, for the rows carrying a `rings` block. */
  const ringSystems = new Map<string, RingsHandle>();
  /** The bodies with rings, so the tick walks four entries and not sixteen. */
  const ringed: Array<{ body: OrreryBody; rings: RingsHandle }> = [];
  let disposed = false;
  let ringsVisible = true;
  let namesVisible = true;
  const layerOn: Record<OrreryLayer, boolean> = {
    orbitsHelio: true, orbitsMoons: true, labels: true, moonLabels: true, rings: true,
    // The ring is what a pilot reads when parking, so the spiral is the layer
    // that has to be asked for.
    spirals: false,
  };
  /** One per satellite, built the first frame the layer is on. */
  const spirals: SatelliteSpiral[] = [];
  /** One per orbiting row, in registry order. */
  const ringTracks: OrbitRingTrack[] = [];
  /** `orbit-rings --rings-epoch` reads what the rebuild pass costs a frame. */
  let ringVertsBuilt = 0;
  let ringVertsTracked = 0;
  let ringRebuildMs = 0;
  let ringRebuildFrames = 0;
  let ringRebuilds = 0;
  let labelSuppress = false;
  let labelNoDeclutter = false;
  let labelSurfaceGate = false;
  /** The camera `update` was last handed, for the screen-space label pass. */
  const camWorld: WorldVec = { x: 0, y: 0, z: 0 };
  // The whole orrery's FIRST draw is a frame's worth of GL that nothing else
  // pays: about 9 ms of program link per distinct material plus Mars's map,
  // 55 ms on the frame the registry resolves. Nothing is drawn until that work
  // is done and exactly one step of it runs per frame, so it costs a tenth of
  // a second of frames inside cadence instead of one stall.
  //
  // And a step only on a frame that had room. The registry lands while the page
  // is still paying for its own boot, where every frame is already over
  // cadence; the deadline is so a machine that never gets under it still ends
  // up with an orrery.
  const WARM_FRAME_MS = 20;
  const WARM_DEADLINE_MS = 4000;
  let drawable = false;
  let lastUpdateMs = 0;
  let firstUpdateMs = 0;
  const warmUp: Array<() => void> = [];
  const linkSeen = new Set<string>();
  /** One per textured globe: each body fills its holes with its OWN colour. */
  const covers: CoverageFillHandle[] = [];
  /** The same handles, by body id, so a pyramid load can retarget its own. */
  const coversById = new Map<string, CoverageFillHandle>();
  /** One per body whose registry row publishes an artificial map. */
  const arts: ArtificialFillHandle[] = [];
  /** The same handles, by body id, so a panel can report what it draws. */
  const artsById = new Map<string, ArtificialFillHandle>();
  /** One per body whose registry row publishes an artificial relief map. */
  const reliefs: ArtificialReliefHandle[] = [];
  const reliefsById = new Map<string, ArtificialReliefHandle>();
  /** Landed Titan-style samplers, so the debug switch below can reach them. */
  const tileReliefSamplers: TileReliefSampler[] = [];
  let tileReliefInertDebug = false;
  let tileReliefUnconditionedDebug = false;
  let tileReliefRepeatDebug = 1;
  // Sol's disc is not a globe and is built above, but its unmeasured polar
  // caps answer to the same override every other body's holes do.
  covers.push(solCover);
  coversById.set('sol', solCover);
  if (solArt !== null) { arts.push(solArt); artsById.set('sol', solArt); }
  /** One link per program cache key: the material instances are all distinct. */
  function enqueueLink(o: THREE.Object3D, mat: THREE.Material): void {
    const map = (mat as THREE.MeshStandardMaterial).map ? '+map' : '';
    // three's own default for this returns `onBeforeCompile`'s source, so an
    // injected chunk - the ring shadow - is its own key and gets its own link.
    const key = `${mat.type}${map}${mat.transparent ? '+t' : ''}`
      + `+${mat.customProgramCacheKey()}`;
    if (linkSeen.has(key)) return;
    linkSeen.add(key);
    warmUp.push(() => config.linkPrograms(o));
  }

  /**
   * This body's artificial map, loaded beside whatever measured map it has.
   *
   * Its alpha is the fill's own coverage - 255 where the fill has data, 0 on
   * every texel an instrument measured - so the composite needs no second mask
   * and a measured texel is the identity whatever the layer says.
   */
  function attachArtificial(material: THREE.MeshStandardMaterial,
    row: BodyRow): ArtificialFillHandle | null {
    const handle = newArtificialFill(row);
    if (handle === null) return null;
    markArtificialFill(material, handle.ref);
    return handle;
  }

  /**
   * One body's handle, registered but with nothing fetched yet.
   *
   * Sol takes this on its own: its disc is not a globe and carries no standard
   * material to mark, so `sol.ts` reads the same three uniforms out of the ref
   * directly.
   */
  function newArtificialFill(row: BodyRow): ArtificialFillHandle | null {
    if ((row.artificial?.map ?? null) === null) return null;
    const handle: ArtificialFillHandle = createArtificialFill(
      false, () => { loadArtificialMap(handle, row); });
    handle.setTransfer(albedoTransferOf(row));
    arts.push(handle);
    artsById.set(row.id, handle);
    return handle;
  }

  const retryTimers = new Set<ReturnType<typeof setTimeout>>();
  own(() => {
    if (globeMapNoCancel()) return; // old behaviour: a queued retry keeps running past dispose
    for (const t of retryTimers) clearTimeout(t);
    retryTimers.clear();
  });
  /** A body map's decode, asked for again on `MAP_RETRY_MS` after a failure. */
  function loadMap(url: string): Promise<ImageBitmap> {
    const attempt = (n: number): Promise<ImageBitmap> => loadFlippedBitmap(url).catch((err: unknown) => {
      const waitMs = MAP_RETRY_MS[n];
      if (waitMs === undefined || disposed || globeMapNoRetry()) throw err;
      return new Promise<ImageBitmap>((resolve) => {
        const timer = setTimeout(() => {
          retryTimers.delete(timer);
          resolve(attempt(n + 1));
        }, waitMs);
        retryTimers.add(timer);
      });
    });
    return attempt(0);
  }

  /** Fetch one body's fill map, the first time its layer asks for it. */
  function loadArtificialMap(handle: ArtificialFillHandle, row: BodyRow): void {
    const src = row.artificial?.map ?? null;
    if (src === null || disposed) return;
    const tex = new THREE.Texture();
    tex.name = `body-artificial:${row.id}`;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;
    tex.flipY = false;
    let bitmap: ImageBitmap | null = null;
    config.onLoadStart();
    void loadMap(src).then((b) => {
      if (disposed) { b.close(); return; }
      bitmap = b;
      tex.image = b;
      tex.needsUpdate = true;
      // Only now does the uniform go to 1: an unloaded sampler reads three's
      // own four zero bytes, and the frame before the map lands has to be the
      // frame the layer was off.
      handle.setMap(tex);
      if (drawable) config.uploadTexture(tex);
      else warmUp.push(() => config.uploadTexture(tex));
      config.onNeedsRender();
      config.onLoadEnd();
    }).catch((err: unknown) => {
      console.warn(`[atlas] ${row.id}'s artificial map did not load; `
        + 'the layer draws nothing on it:', err);
      config.onLoadEnd();
    });
    own(() => { tex.dispose(); bitmap?.close(); });
  }

  /**
   * This body's artificial relief, marked on the same material its imagery is.
   *
   * The map carries slopes and no colour, so it is read raw: an sRGB decode
   * here would bend every slope towards zero and the shading would read as a
   * fraction of the relief the bake published.
   */
  function attachArtificialRelief(material: THREE.MeshStandardMaterial,
    row: BodyRow): ArtificialReliefHandle | null {
    const art = row.artificial_height;
    if (art === undefined || art.slope_full_scale === undefined) return null;
    const handle: ArtificialReliefHandle = createArtificialRelief(
      art.slope_full_scale, () => { loadReliefMap(handle, row); });
    reliefs.push(handle);
    reliefsById.set(row.id, handle);
    markArtificialRelief(material, handle.ref);
    return handle;
  }

  /** Fetch one body's relief map, the first time its layer asks for it. */
  function loadReliefMap(handle: ArtificialReliefHandle, row: BodyRow): void {
    const src = row.artificial_height?.map ?? null;
    if (src === null || disposed) return;
    const tex = new THREE.Texture();
    tex.name = `body-relief:${row.id}`;
    tex.colorSpace = THREE.NoColorSpace;
    tex.anisotropy = 8;
    tex.flipY = false;
    let bitmap: ImageBitmap | null = null;
    config.onLoadStart();
    void loadMap(src).then((b) => {
      if (disposed) { b.close(); return; }
      bitmap = b;
      tex.image = b;
      tex.needsUpdate = true;
      handle.setMap(tex);
      if (drawable) config.uploadTexture(tex);
      else warmUp.push(() => config.uploadTexture(tex));
      config.onNeedsRender();
      config.onLoadEnd();
    }).catch((err: unknown) => {
      console.warn(`[atlas] ${row.id}'s artificial relief map did not load; `
        + 'the layer shades nothing on it:', err);
      config.onLoadEnd();
    });
    own(() => { tex.dispose(); bitmap?.close(); });
  }

  /**
   * Load a `classes`-only body's two terrain-class maps and build its
   * procedural sampler, the way `loadReliefMap` loads a baked one's single
   * slope map. Nothing reads `body.tileRelief` yet - a later step remeshes the
   * tile band off `body.tileReliefVersion`.
   */
  function attachTileRelief(row: BodyRow, body: OrreryBody): void {
    const art = row.artificial_height;
    if (art === undefined || art.classes === undefined || disposed) return;
    const classes = art.classes;
    let cancelled = false;
    config.onLoadStart();
    Promise.all([
      decodeClassMapWebp(classes.terrain_map),
      decodeClassMapWebp(classes.dune_dir_map),
    ]).then(([terrainMap, duneMap]) => {
      if (disposed || cancelled) return;
      const sampler = createTileReliefSampler(terrainMap, duneMap, {
        radiusKm: row.radius_m / 1000,
        seed: art.seed,
        craters: classes.craters,
        // Always on: the tile band's own switch gates it, and a flag captured here
        // would leave every tile flat after the row is turned on later.
        on: true,
      });
      sampler.reliefInert = tileReliefInertDebug;
      sampler.reliefUnconditioned = tileReliefUnconditionedDebug;
      sampler.reliefRepeat = tileReliefRepeatDebug;
      tileReliefSamplers.push(sampler);
      body.tileRelief = sampler;
      body.tileReliefVersion += 1;
      config.onNeedsRender();
      config.onLoadEnd();
    }).catch((err: unknown) => {
      console.warn(`[atlas] ${row.id}'s terrain-class maps did not load; `
        + 'the tile band generates no procedural relief:', err);
      config.onLoadEnd();
    });
    own(() => { cancelled = true; });
  }

  /** Each body's undisplaced sphere, kept so the shape can be swapped back. */
  const spheres = new Map<string, THREE.BufferGeometry>();
  /** The registry ellipsoid a shaped body draws as until its shape lands. */
  const standIns = new Map<string, THREE.BufferGeometry>();
  let roundGlobes = false;
  const unshaped = (bodyId: string): THREE.BufferGeometry | null =>
    (roundGlobes ? null : standIns.get(bodyId)) ?? spheres.get(bodyId) ?? null;
  /** Whose displaced mesh has already been paid a program link for. */
  const linked = new Set<string>();
  /** The datum each drawn shape's relief is measured from, metres. */
  const shapeDatums = new Map<string, number>();
  function setDrawRadius(bodyId: string, radiusM: number): void {
    const b = byId.get(bodyId);
    if (!b || !(radiusM > 0) || b.drawRadiusM === radiusM) return;
    b.drawRadiusM = radiusM;
    config.onNeedsRender();
  }
  function setDatumRadius(bodyId: string, datumRadiusM: number): void {
    if (!byId.has(bodyId) || !(datumRadiusM > 0) || registryDraw()) return;
    shapeDatums.set(bodyId, datumRadiusM);
    setDrawRadius(bodyId, datumRadiusM);
  }

  for (const row of rows) {
    if (row.orbit === null) continue; // the frame origin is Sol's own disc
    const geo = new THREE.SphereGeometry(
      row.radius_m, GLOBE_WIDTH_SEGMENTS, GLOBE_HEIGHT_SEGMENTS);
    spheres.set(row.id, geo);
    if (ellipsoidStandsIn(row)) {
      standIns.set(row.id, ellipsoidGeometry(row, GLOBE_WIDTH_SEGMENTS, GLOBE_HEIGHT_SEGMENTS));
    }
    let mat: THREE.Material;
    let cover: CoverageFillHandle | null = null;
    let art: ArtificialFillHandle | null = null;
    let rel: ArtificialReliefHandle | null = null;
    // Before any hook writer: each reads it off the material.
    const shadow = shadows.refFor(row.id);
    if (row.texture) {
      config.onLoadStart();
      // A `map` with no image samples three's empty texture, which is four zero
      // bytes, so the globe drew BLACK for as long as its map was in flight and
      // for ever when the request failed. One texel of the row's own
      // albedo_color stands in until the map lands - the field's own
      // definition. A second texture rather than this one's image, because
      // three allocates immutable storage at the first upload and a 2,048 px
      // image cannot then go into a 1 px allocation.
      const flat = new THREE.Texture(flatTexel(row.albedo_color));
      flat.name = `body-flat:${row.id}`;
      flat.colorSpace = THREE.SRGBColorSpace;
      flat.needsUpdate = true;
      const map = new THREE.Texture();
      map.name = `body-map:${row.id}`;
      const transfer = albedoTransferOf(row);
      map.colorSpace = albedoColorSpace(transfer);
      map.anisotropy = 8;
      map.flipY = false;
      let bitmap: ImageBitmap | null = null;
      // The sim's own scene carries an image-based environment map and these
      // globes are drawn in it in cruise, which lifts a planet's night side off
      // a light that is not the sun. Nothing lights a body here but the Atlas's
      // sun and, where it is drawn, the body's own air.
      const material = new THREE.MeshStandardMaterial({
        map: flat, roughness: GROUND_ROUGHNESS, metalness: GROUND_METALNESS, envMapIntensity: 0,
      });
      if (shadow) markBodyShadow(material, shadow);
      // The globe map's alpha is its coverage, and this is where a control
      // gets to substitute for a texel no instrument measured (the map already
      // carries the bake's own fill there). Unconditional, so the program cache
      // key gains a constant rather than a second variant: a map with no alpha
      // plane reads back fully covered and the chunk is inert.
      cover = createCoverageFill(row.albedo_color);
      covers.push(cover);
      coversById.set(row.id, cover);
      art = attachArtificial(material, row);
      rel = attachArtificialRelief(material, row);
      // Off over the flat stand-in, which is the row's own sRGB colour.
      const albedoDn = albedoTransferUniform(transfer, false);
      if (transfer) attachAlbedoTransfer(material, albedoDn);
      globeFillChunks(material, cover.ref, art?.ref ?? null, rel?.ref ?? null);
      void loadMap(row.texture).then((b) => {
        if (disposed) { b.close(); return; }
        bitmap = b;
        map.image = b;
        map.needsUpdate = true;
        // Both shapes carry a map, so this swap keeps the program the warm-up
        // linked and never pays a link on the frame the image arrives.
        material.map = map;
        albedoDn.value.z = transfer ? 1 : 0;
        material.needsUpdate = true;
        flat.dispose();
        // Its own frame, whether the warm-up is still draining or long done.
        if (drawable) config.uploadTexture(map);
        else warmUp.push(() => config.uploadTexture(map));
        config.onNeedsRender();
        config.onLoadEnd();
      }).catch((err: unknown) => {
        console.warn(`[atlas] ${row.id}'s globe map did not load; `
          + 'the globe is its albedo colour:', err);
        config.onLoadEnd();
      });
      mat = material;
      own(() => { flat.dispose(); map.dispose(); bitmap?.close(); });
    } else if (row.artificial) {
      // A bare body's only imagery IS the artificial map, and the composite
      // reads `vMapUv`, which three declares only on a material that carries a
      // map at all. One texel of the row's own albedo_color is that map, so the
      // layer off draws exactly what the flat branch below draws.
      const flat = new THREE.Texture(flatTexel(row.albedo_color));
      flat.name = `body-flat:${row.id}`;
      flat.colorSpace = THREE.SRGBColorSpace;
      flat.needsUpdate = true;
      const material = new THREE.MeshStandardMaterial({
        map: flat, roughness: 0.85, metalness: 0.05, envMapIntensity: 0,
      });
      if (shadow) markBodyShadow(material, shadow);
      cover = createCoverageFill(row.albedo_color);
      covers.push(cover);
      coversById.set(row.id, cover);
      art = attachArtificial(material, row);
      rel = attachArtificialRelief(material, row);
      globeFillChunks(material, cover.ref, art?.ref ?? null, rel?.ref ?? null);
      mat = material;
      own(() => { flat.dispose(); });
    } else {
      mat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(row.albedo_color), roughness: 0.85, metalness: 0.05,
        envMapIntensity: 0,
      });
      if (shadow) {
        markBodyShadow(mat, shadow);
        applyBodyShadowOnly(mat, shadow);
      }
    }
    const mesh = new THREE.Mesh(unshaped(row.id) ?? geo, mat);
    mesh.name = row.id;
    mesh.userData['bodyId'] = row.id;
    mesh.userData['available'] = row.availability.descend;
    mesh.userData['pickBand'] = row.pick_band;
    // A body at a metre-scale distance is culled correctly, but the frame
    // writes its position after the cull would run on a stale matrix.
    mesh.frustumCulled = false;
    const body: OrreryBody = {
      row,
      mesh,
      drawRadiusM: row.radius_m,
      world: { x: 0, y: 0, z: 0 },
      quaternion: new THREE.Quaternion(),
      rotationKnown:
        row.rotation.pole_ra_deg !== null && row.rotation.pole_dec_deg !== null &&
        row.rotation.prime_meridian_deg !== null && row.rotation.W_rate_deg_day !== null,
      coverage: cover ? cover.ref : null,
      artificial: art ? art.ref : null,
      relief: rel && row.artificial_height?.classes === undefined ? rel.ref : null,
      shadow,
      tileRelief: null,
      tileReliefVersion: 0,
      satellite: (row.orbit?.parent_relative ?? false) && row.parent !== null,
      orbitLine: null,
    };
    const unregister = frame.register(mesh, () => body.world);
    own(() => {
      unregister();
      (spheres.get(row.id) ?? geo).dispose();
      standIns.get(row.id)?.dispose();
      mat.dispose();
    });

    // The ring system, as a CHILD of the body sphere: the pole quaternion, the
    // camera-relative position and the angular-floor scale are then the body's
    // own, and the same `rings.update` moves both the annulus and the shadow
    // the annulus throws on the sphere.
    if (row.rings) {
      // One handle per component, in the component list's own order: the
      // generated `ringTauAt` addresses a component by index, so a list that
      // slipped by one would sample another component's texture.
      const profiles = row.rings.components.map((component) => {
        if (!component.profile) return null;
        config.onLoadStart();
        const p = createRingProfile(row, component);
        const tex = p.texture;
        void p.ready.then(() => {
          if (disposed) return;
          // Its own frame, for the reason the body map takes one: 147 KB is a
          // blit, but on the frame that would first draw it, it is a blit
          // behind a program link.
          if (drawable) config.uploadTexture(tex);
          else warmUp.push(() => config.uploadTexture(tex));
          config.onNeedsRender();
          config.onLoadEnd();
        }).catch((err: unknown) => {
          console.warn(`[atlas] ${row.id}'s ${component.name} ring profile did not load:`, err);
          config.onLoadEnd();
        });
        return p;
      });
      const rings = createRings({
        row, profiles, sunIntensity: sunLight.intensity, sunColor: sunLight.color,
      });
      mesh.add(rings.mesh);
      applyRingShadow(mat as THREE.MeshStandardMaterial, rings, row.id);
      ringSystems.set(row.id, rings);
      ringed.push({ body, rings });
      own(() => {
        ringSystems.delete(row.id);
        rings.dispose();
        for (const p of profiles) p?.dispose();
      });
    }

    // The name, on every body. What it says is which body this is, so nothing
    // about a body's own availability gates it; that is the name's COLOUR.
    {
      const text = row.display_name.toUpperCase();
      const navigable = row.availability.descend;
      const label = bodyNameTexture(text, navigable ? NAVIGABLE_CSS : LABEL_PLAIN_CSS);
      // A picture, not data - the glyphs are drawn straight in their own
      // colour, so it takes the `imagery` role `body-map:` already carries.
      if (label) label.texture.name = `body-map:${row.id}-name`;
      const labelMat = new THREE.SpriteMaterial({
        map: label?.texture ?? null, transparent: true, toneMapped: inkToneMapped(),
        depthTest: false, depthWrite: false, sizeAttenuation: false,
      });
      const sprite = new THREE.Sprite(labelMat);
      sprite.name = `${row.id}-name`;
      sprite.frustumCulled = false;
      sprite.renderOrder = 3;
      sprite.visible = false;
      // A name is not a target. Sprites answer a raycast by default, and one
      // over a body's own dot took the click that the dot was meant to take.
      sprite.raycast = () => {};
      // Screen size, not world size: the scale of a sprite with size
      // attenuation off IS its angular size in radians, and the off-centre
      // anchor lifts the name clear of the ring.
      const labelRad = (NAME_HEIGHT_DEG * Math.PI) / 180;
      sprite.scale.set(labelRad * (label?.aspect ?? 4), labelRad, 1);
      sprite.center.set(0.5, -0.9);
      const unregisterLabel = frame.register(sprite, () => body.world);
      labels.set(row.id, {
        id: row.id, sprite, mat: labelMat, text,
        parent: row.orbit?.parent_relative ? (row.parent ?? null) : null,
        discFade: 0, gated: false, trueHalfDeg: 0, sortKey: 0,
        left: 0, top: 0, right: 0, bottom: 0, nl: 0, nr: 0, nt: 0, nb: 0,
        clearPx: -1, opacity: 0, boxed: false,
      });
      own(() => {
        unregisterLabel();
        labels.delete(row.id);
        label?.texture.dispose();
        labelMat.dispose();
      });
    }

    // The orbit ring, sampled from the same elements over one period. Every
    // orbiting row gets one; what changes with the hierarchy is the frame the
    // samples are taken in, the point it is registered at, and its colour.
    const satellite = body.satellite;
    const period = periodDays(row);
    if (period > 0) {
      const orbit = row.orbit;
      const verts = new Float32Array(RING_SEGS * 3);
      const ringGeo = new THREE.BufferGeometry();
      ringGeo.setAttribute('position', new THREE.BufferAttribute(verts, 3));
      const track: OrbitRingTrack = {
        row, orbit, satellite, period, verts, geo: ringGeo, body,
        sagUnits: orbit.a_au * AU_KM * UNITS_PER_KM * (1 - Math.cos(Math.PI / RING_SEGS)),
        scaleUnits: orbit.a_au * AU_KM * UNITS_PER_KM,
        driftUnitsPerDay: ellipseDriftKmPerDay(orbit) * UNITS_PER_KM,
        anchor: { x: 0, y: 0, z: 0 },
        point: { x: 0, y: 0, z: 0 },
        builtJd: Number.NaN,
        builtE: Number.NaN,
        elementsStale: false,
      };
      // The control's frozen behaviour: sampled once at the row's own
      // published epoch and never rebuilt after, which is what shipped.
      buildRing(track, ringsFrozenEpoch() ? orbit.epoch_jd : config.jdTdb());
      ringTracks.push(track);
      const ringMat = new THREE.LineBasicMaterial({
        color: satellite ? SATELLITE_ORBIT_COLOR : NAVIGABLE_COLOR,
        transparent: true, toneMapped: inkToneMapped(),
        opacity: satellite ? SATELLITE_ORBIT_OPACITY : HELIO_ORBIT_OPACITY,
      });
      ringMat.userData['baseOpacity'] = ringMat.opacity;
      const ring = new THREE.LineLoop(ringGeo, ringMat);
      ring.name = `${row.id}-orbit`;
      ring.frustumCulled = false;
      // A heliocentric ring is registered at its own anchor, the body's
      // position when it was built; a satellite's at that anchor carried by
      // its parent's LIVE position, so the ring follows the planet round the
      // Sun. The parent is looked up per frame rather than captured, so
      // registry order cannot leave one at the origin. A barycentre's ring is
      // carried by the live step to its own centre, so Earth stays on the
      // ellipse the Earth-Moon barycentre runs.
      const parentId = satellite ? row.parent : null;
      const unregisterRing = parentId === null
        ? frame.register(ring, orbit.barycentre_with === undefined || ringsFrozenEpoch()
          ? () => track.anchor
          : () => {
            const step = eclipticToScene(barycentreStep(row, rows, config.jdTdb(), _baryStep).position, UNITS_PER_KM);
            track.point.x = track.anchor.x + step.x;
            track.point.y = track.anchor.y + step.y;
            track.point.z = track.anchor.z + step.z;
            return track.point;
          })
        : frame.register(ring, () => {
          const p = byId.get(parentId)?.world ?? ORIGIN;
          track.point.x = p.x + track.anchor.x;
          track.point.y = p.y + track.anchor.y;
          track.point.z = p.z + track.anchor.z;
          return track.point;
        });
      body.orbitLine = ring;
      own(() => {
        unregisterRing();
        ringGeo.dispose();
        ringMat.dispose();
      });
    }

    // The spiral the ring is a picture of the alternative to: the same moon's
    // path in the SUN's frame, so the parent's own travel is in it. Registered
    // at its own anchor and not at the parent, because that path is fixed in
    // the Sun's frame while the parent runs along it. Empty until the layer is
    // first switched on - a window nobody asked for is 1,536 Kepler solves.
    if (satellite && period > 0) {
      // Odd, so one sample lands ON the centre epoch: the moon then stands on a
      // VERTEX of its own trace the instant the window is built, rather than
      // halfway along a chord, which is the one place a polyline is furthest
      // from the curve it draws.
      const n = SPIRAL_SAMPLES_PER_TURN * SPIRAL_TURNS_EITHER_SIDE * 2 + 1;
      const spiralVerts = new Float32Array(n * 3);
      const spiralGeo = new THREE.BufferGeometry();
      spiralGeo.setAttribute('position', new THREE.BufferAttribute(spiralVerts, 3));
      const spiralMat = new THREE.LineBasicMaterial({
        color: SPIRAL_COLOR, transparent: true, toneMapped: inkToneMapped(),
        opacity: SPIRAL_OPACITY,
      });
      spiralMat.userData['baseOpacity'] = spiralMat.opacity;
      const spiralLine = new THREE.Line(spiralGeo, spiralMat);
      spiralLine.name = `${row.id}-spiral`;
      spiralLine.frustumCulled = false;
      spiralLine.visible = false;
      const sp: SatelliteSpiral = {
        row,
        line: spiralLine,
        geo: spiralGeo,
        verts: spiralVerts,
        anchor: { x: 0, y: 0, z: 0 },
        halfWindowDays: period * SPIRAL_TURNS_EITHER_SIDE,
        builtJd: Number.NaN,
        elementsStale: false,
      };
      const unregisterSpiral = frame.register(spiralLine, () => sp.anchor);
      spirals.push(sp);
      own(() => {
        unregisterSpiral();
        spiralGeo.dispose();
        spiralMat.dispose();
      });
    }

    bodies.push(body);
    byId.set(row.id, body);
    attachTileRelief(row, body);
  }

  enqueueLink(sol.mesh, sol.mesh.material as THREE.Material);
  for (const b of bodies) {
    enqueueLink(b.mesh, b.mesh.material as THREE.Material);
    const rings = ringSystems.get(b.row.id);
    if (rings) {
      const rm = rings.mesh;
      // Its own step rather than through `enqueueLink`: `compile` walks only
      // what is VISIBLE, and the tick hides an annulus whose true angular
      // width is sub-pixel - which the opening heliocentric framing always is.
      warmUp.push(() => {
        const was = rm.visible;
        rm.visible = true;
        config.linkPrograms(rm);
        rm.visible = was;
      });
    }
    if (b.orbitLine) enqueueLink(b.orbitLine, b.orbitLine.material as THREE.Material);
    const lb = labels.get(b.row.id);
    if (lb) enqueueLink(lb.sprite, lb.sprite.material);
  }

  const _ringSun = new THREE.Vector3();
  const _ringCam = new THREE.Vector3();
  const _ringQ = new THREE.Quaternion();
  const _toCam = new THREE.Vector3();
  const _zero = new THREE.Vector3();
  const _yUp = new THREE.Vector3(0, 1, 0);
  const _face = new THREE.Matrix4();
  const _labelV = new THREE.Vector3();
  const _labelQ = new THREE.Quaternion();
  const _solQ = new THREE.Quaternion();
  let lastJd = Number.NaN;
  // Reused across every body each `propagate()` tick: `heliocentric`'s own
  // out-parameter. `_earthHelio` is a copy taken out of it when the row is
  // earth, since `_helio` itself gets overwritten by every later body in the
  // same sweep and `earthHelioPos` below is read only after the loop ends.
  const _helio = { position: { x: 0, y: 0, z: 0 }, velocity: { x: 0, y: 0, z: 0 } };
  const _earthHelio = { x: 0, y: 0, z: 0 };

  function orient(body: OrreryBody, jd: number): void {
    bodyFixedQuaternion(body.row, jd, body.quaternion);
    body.mesh.quaternion.copy(body.quaternion);
  }

  const handle: OrreryHandle = {
    bodies,
    byId: (id: string) => byId.get(id),
    sol,
    lights,
    jdTdb: () => config.jdTdb(),
    propagate(): void {
      // Ahead of the early out: pausing leaves the date still and still has to
      // bring back the granulation a fast warp faded.
      if (solPhoto.setClockRate(config.clockRate())) config.onNeedsRender();
      const jd = config.jdTdb();
      if (jd === lastJd) return;
      lastJd = jd;
      let earthHelioPos: Vec3 | null = null;
      for (const b of bodies) {
        heliocentric(b.row, rows, jd, _helio);
        if (b.row.id === 'earth') {
          _earthHelio.x = _helio.position.x;
          _earthHelio.y = _helio.position.y;
          _earthHelio.z = _helio.position.z;
          earthHelioPos = _earthHelio;
        }
        const p = eclipticToScene(_helio.position, UNITS_PER_KM);
        b.world.x = p.x;
        b.world.y = p.y;
        b.world.z = p.z;
        orient(b, jd);
      }
      // The disc's map turns with W the same way every globe's does. Sol has
      // no OrreryBody to hold a quaternion, so this is the one place it is
      // derived, off the same `bodyFixedQuaternion` the globes take.
      // Held under the live layer's own control: the whole of what the drawn
      // photosphere reads off the clock is the drift the shader carries, the
      // granulation generation and this rotation, so a control that holds the
      // first two and lets the disc turn would not hold the frame.
      if (solRow && !solFrozen && bodyFixedQuaternion(solRow, jd, _solQ)) {
        sol.setBodyQuaternion(_solQ);
      }
      // After the quaternion, which is the frame the sub-Earth longitude is
      // read in. The region list is rebuilt only when the date has moved far
      // enough; the shader carries the rotation between rebuilds.
      solPhoto.update(jd, subEarthBodyLonRad(earthHelioPos));
      // The rings are rebuilt in `update` rather than here: the gate that says
      // which of them are worth a rebuild this frame is a PIXEL size, and the
      // camera is what this call does not have.
    },
    update(cameraWorld: WorldVec): void {
      handle.propagate();
      shadows.update(bodies);
      camWorld.x = cameraWorld.x;
      camWorld.y = cameraWorld.y;
      camWorld.z = cameraWorld.z;
      // Sol takes the camera's heliocentric position in km and places itself.
      sol.update({
        x: cameraWorld.x / UNITS_PER_KM,
        y: cameraWorld.y / UNITS_PER_KM,
        z: cameraWorld.z / UNITS_PER_KM,
      });
      solPhoto.setDrawnAngularDiameterDeg(sol.state.drawnAngularDiameterDeg);
      sunLight.position.set(-cameraWorld.x, -cameraWorld.y, -cameraWorld.z);
      if (!drawable) {
        const nowMs = performance.now();
        const frameMs = lastUpdateMs === 0 ? Infinity : nowMs - lastUpdateMs;
        lastUpdateMs = nowMs;
        if (firstUpdateMs === 0) firstUpdateMs = nowMs;
        if (frameMs < WARM_FRAME_MS || nowMs - firstUpdateMs > WARM_DEADLINE_MS) {
          const step = warmUp.shift();
          if (step) step();
          else drawable = true;
        }
        config.onNeedsRender();
      }
      sol.mesh.visible = drawable;
      for (let i = 0; i < bodies.length; i++) {
        const b = bodies[i]!;
        b.mesh.quaternion.copy(b.quaternion);
        const d = Math.hypot(
          b.world.x - cameraWorld.x, b.world.y - cameraWorld.y, b.world.z - cameraWorld.z,
        );
        // The geometry is built at the registry's radius, so a draw radius that
        // is not that one rides in as a scale under the angular floor's own.
        const rDraw = b.drawRadiusM;
        const trueHalf = d > rDraw ? Math.asin(rDraw / d) : Math.PI / 2;
        const unshapedNow = b.mesh.geometry === spheres.get(b.row.id)
          || b.mesh.geometry === standIns.get(b.row.id);
        b.mesh.visible = drawable && !(config.shapeHold && unshapedNow
          && config.shapeHold(b.row.id, (2 * trueHalf) / DEG));
        const drawnHalf = Math.max(trueHalf, minHalfAngle);
        const k = drawnHalf > trueHalf ? (d * Math.tan(drawnHalf)) / rDraw : 1;
        b.mesh.scale.setScalar((k * rDraw) / b.row.radius_m);
        b.mesh.userData['trueAngularDiameterDeg'] = (2 * trueHalf) / DEG;
        b.mesh.userData['drawnAngularDiameterDeg'] = (2 * drawnHalf) / DEG;
        b.mesh.userData['distanceKm'] = d / UNITS_PER_KM;
        // A satellite draws one line or the other, never both: the spiral is
        // the same orbit in another frame, and the two crossing each other at
        // the moon says nothing either of them says alone.
        const orbitLayer = b.satellite
          ? layerOn.orbitsMoons && !layerOn.spirals
          : layerOn.orbitsHelio;
        if (b.orbitLine) b.orbitLine.visible = ringsVisible && drawable && orbitLayer;
        // Faded out as the disc grows past a dot: a name over something the
        // eye has already found and can read the shape of says nothing.
        const fade = 1 - subPixelFade(
          drawnHalf / DEG, NAME_FADE_FULL_HALF_DEG, NAME_FADE_GONE_HALF_DEG,
        );
        const lab = labels.get(b.row.id);
        if (lab) {
          lab.discFade = fade;
          // The focus keeps its name too: at these bands it is still a dot
          // among dots and the name is what says which dot.
          const nameLayer = b.satellite ? layerOn.moonLabels && layerOn.labels : layerOn.labels;
          lab.gated = drawable && namesVisible && nameLayer && fade > 0;
          lab.trueHalfDeg = trueHalf / DEG;
          // Off until `layoutLabels` has solved a box for it this frame, so a
          // name never survives on last frame's placement.
          lab.sprite.visible = false;
          lab.opacity = 0;
          lab.clearPx = -1;
          lab.boxed = false;
        }
      }
      // Every ring that is DRAWN, held on the body it belongs to. A ring nobody
      // can see is not touched at all. One drawn close enough that its own
      // chord sag would show has the run beside the body re-placed, which is
      // five vertices; the whole turn is re-sampled only once that run has slid
      // as far as it may, the ellipse has precessed out from under it, or the
      // elements have gone stale, which is why the whole-system fit costs next
      // to nothing and focus zoom now costs little more.
      if (!ringsFrozenEpoch()) {
        const t0 = performance.now();
        for (const track of ringTracks) {
          const line = track.body.orbitLine;
          if (line === null || !line.visible) continue;
          const b = track.body;
          const d = Math.hypot(
            b.world.x - cameraWorld.x, b.world.y - cameraWorld.y, b.world.z - cameraWorld.z,
          );
          const perUnit = 1 / Math.max(1e-9, d) / REF_PIXEL_RAD;
          const ringPerUnit = 1 / Math.max(1e-9, d, track.scaleUnits) / REF_PIXEL_RAD;
          const aged = Math.abs(lastJd - track.builtJd);
          // The control sizes both terms at the body's own pixel again, which is
          // what made the sag term true on every frame a body was focused.
          const driftPerUnit = ringRebuildEveryFrame() ? perUnit : ringPerUnit;
          let whole = track.elementsStale
            || !(aged <= RING_STALE_DAYS)
            || track.driftUnitsPerDay * aged * driftPerUnit >= RING_DRIFT_PX
            || (ringRebuildEveryFrame() && track.sagUnits * perUnit >= RING_GATE_PX);
          // A control that wants the body BETWEEN vertices cannot also have one
          // planted on it, so both of those turn the run off rather than gate it.
          const localOff = ringNoLocalTrack() || ringsUniformPhase()
            || ringRebuildEveryFrame();
          if (!whole && !localOff && track.sagUnits * perUnit >= RING_GATE_PX) {
            whole = !trackRing(track, lastJd);
          }
          if (whole) {
            buildRing(track, lastJd);
            ringRebuilds++;
          }
        }
        ringRebuildMs += performance.now() - t0;
        ringRebuildFrames++;
      }
      // The window follows the clock by RE-CENTRING, not by rebuilding: a
      // re-sample costs 1,536 Kepler solves, and the moon stays on a stale
      // trace until it runs off the end of it, so what goes wrong first is the
      // picture sliding off centre rather than the moon leaving the line.
      for (const sp of spirals) {
        const want = spiralStuckOn()
          ? drawable && ringsVisible
          : drawable && ringsVisible && layerOn.spirals
            && (!spiralNeedsOrbits() || layerOn.orbitsMoons);
        if (want) {
          const drift = Math.abs(lastJd - sp.builtJd);
          const aged = !(drift <= SPIRAL_RECENTRE_FRACTION * sp.halfWindowDays);
          if ((aged || sp.elementsStale)
            && !(spiralPinned() && Number.isFinite(sp.builtJd))) {
            buildSpiral(sp, lastJd);
          }
        }
        sp.line.visible = want;
      }
      for (const r of ringed) {
        const b = r.body;
        const d = Math.hypot(
          b.world.x - cameraWorld.x, b.world.y - cameraWorld.y, b.world.z - cameraWorld.z,
        );
        // Both vectors into the body-fixed frame, and the camera into the
        // geometry's own metres: the sphere and its ring are drawn at the
        // angular floor's scale, and a view direction taken against a scaled
        // position is not the direction the fragment is looking along.
        _ringQ.copy(b.quaternion).invert();
        _ringSun.set(-b.world.x, -b.world.y, -b.world.z);
        if (_ringSun.lengthSq() > 0) _ringSun.normalize().applyQuaternion(_ringQ);
        else _ringSun.set(0, 1, 0);
        _ringCam
          .set(cameraWorld.x - b.world.x, cameraWorld.y - b.world.y, cameraWorld.z - b.world.z)
          .applyQuaternion(_ringQ)
          .divideScalar(Math.max(1e-9, b.mesh.scale.x));
        // Always, and not only when the annulus is drawn: the shadow the rings
        // throw ACROSS the planet is as wide as the planet whenever the planet
        // is on screen, whatever the annulus itself covers.
        r.rings.update(_ringSun, _ringCam);
        const trueOuterDeg = (2 * Math.atan(r.rings.outerKm * 1000 / Math.max(1, d))) / DEG;
        // Faded rather than toggled: the body under it is held at the angular
        // floor, so what a hard gate on the TRUE size switches off is several
        // pixels of drawn annulus.
        const ringFade = subPixelFade(
          trueOuterDeg, RING_MIN_ANGULAR_DIAMETER_DEG, RING_FULL_ANGULAR_DIAMETER_DEG);
        r.rings.setFade(ringFade);
        r.rings.mesh.visible = drawable && ringFade > 0 && layerOn.rings;
        b.mesh.userData['ringOuterAngularDiameterDeg'] = trueOuterDeg;
      }
    },
    ringsFor(id: string): RingsHandle | undefined {
      return ringSystems.get(id);
    },
    shadows,
    setSunScale(scale: number, groundShare = 1, groundWeight = 0): void {
      shadows.setEyeShare(groundShare, groundWeight);
      const intensity = ORRERY_SUN_INTENSITY * scale;
      if (sunLight.intensity === intensity) return;
      sunLight.intensity = intensity;
      ambient.intensity = ORRERY_AMBIENT_INTENSITY * scale;
      for (const r of ringSystems.values()) r.uniforms['uRingSunRadiance']!.value = intensity / (4 * Math.PI);
      config.onNeedsRender();
    },
    setSunTint(tint: THREE.Vector3 | null): void {
      _sunTinted.set(ORRERY_SUN_COLOR);
      if (tint) _sunTinted.multiply(_sunTintColor.setRGB(tint.x, tint.y, tint.z));
      if (sunLight.color.equals(_sunTinted)) return;
      sunLight.color.copy(_sunTinted);
      config.onNeedsRender();
    },
    setRingDebug(flatTau: number | null): void {
      for (const r of ringed) r.rings.setFlatTau(flatTau);
    },
    setRingAlbedo(albedo: number | null): void {
      for (const r of ringed) r.rings.setAlbedo(albedo);
    },
    setRingPointSample(on: boolean): void {
      for (const r of ringed) r.rings.setPointSample(on);
    },
    setRingGain(factor: number | null): void {
      for (const r of ringed) r.rings.setGain(factor);
    },
    setRingSuppressed(on: boolean): void {
      for (const r of ringed) r.rings.setSuppressed(on);
    },
    setRingComponentScale(index: number | null, factor: number): void {
      for (const r of ringed) r.rings.setComponentScale(index, factor);
    },
    refreshElements(bodyId: string): void {
      for (const track of ringTracks) {
        if (track.row.id !== bodyId) continue;
        // The gate terms sized off `a` at construction stand: an osculating
        // semi-major axis is within a fraction of a percent of the secular
        // one, which is not the part of the ellipse that moved.
        track.elementsStale = true;
      }
      // The spiral is traced from the same elements and re-centres on the
      // clock alone, so a paused clock would hold a window built from the
      // secular fit.
      for (const sp of spirals) {
        if (sp.row.id === bodyId) sp.elementsStale = true;
      }
      config.onNeedsRender();
    },
    readRingRebuild() {
      const out = {
        frames: ringRebuildFrames, ms: ringRebuildMs, rebuilds: ringRebuilds,
        verts: ringVertsBuilt, rings: ringTracks.length, tracked: ringVertsTracked,
      };
      ringRebuildFrames = 0;
      ringRebuildMs = 0;
      ringRebuilds = 0;
      ringVertsBuilt = 0;
      ringVertsTracked = 0;
      return out;
    },
    setRingsVisible(on: boolean): void {
      ringsVisible = on;
    },
    setUnmeasuredFill(cssColor: string | null): void {
      for (const c of covers) c.setUnmeasured(cssColor);
      config.onNeedsRender();
    },
    setCoverageFill(bodyId: string, cssColor: string, transfer: AlbedoTransfer | null = null): void {
      coversById.get(bodyId)?.setBody(cssColor, transfer);
      config.onNeedsRender();
    },
    setArtificialFill(on: boolean): void {
      for (const a of arts) a.setOn(on);
      for (const r of reliefs) r.setOn(on);
      // The same rule, on the one body whose artificial content is a region
      // list rather than a map: off means a measured-only disc.
      solPhoto.setArtificialOn(on);
      config.onNeedsRender();
    },
    loadSolPhotosphere(): void { solPhoto.load(); },
    setSolLive(on: boolean): void {
      solPhoto.setLive(on);
      config.onNeedsRender();
    },
    setSolPhotosphereDebug(d: {
      frozen?: boolean; noSpots?: boolean; eagerRecord?: boolean; whiteDisc?: boolean;
      rate?: number | null; strobe?: boolean;
    }): void {
      if (d.frozen !== undefined) solFrozen = d.frozen;
      solPhoto.setDebug(d);
      config.onNeedsRender();
    },
    solPhotosphereReading(): SolPhotosphereReading { return solPhoto.reading(); },
    solRegionDisc(): readonly SolRegionDisc[] {
      const m = sol.toBodyMatrix().elements;
      return solPhoto.drawn().map((r) => {
        const sl = Math.sin(r.latDeg * Math.PI / 180);
        const cl = Math.sqrt(Math.max(0, 1 - sl * sl));
        const qx = cl * Math.cos(r.lonBodyRad);
        const qy = sl;
        const qz = -cl * Math.sin(r.lonBodyRad);
        // The transpose of the disc's own rotation: a body-fixed direction back
        // to the (x, y, mu) the billboard is drawn in.
        return {
          id: r.id,
          measured: r.measured,
          areaMillionths: r.areaMillionths,
          radiusDeg: r.radiusDeg,
          umbraRadiusDeg: r.umbraRadiusDeg,
          x: m[0]! * qx + m[1]! * qy + m[2]! * qz,
          y: m[3]! * qx + m[4]! * qy + m[5]! * qz,
          mu: m[6]! * qx + m[7]! * qy + m[8]! * qz,
        };
      });
    },
    artificialDrawn(bodyId: string): boolean {
      return artsById.get(bodyId)?.drawn() ?? false;
    },
    artificialReliefDrawn(bodyId: string): boolean {
      return reliefsById.get(bodyId)?.drawn() ?? false;
    },
    artificialCount(): { drawn: number; total: number } {
      return { drawn: arts.filter((a) => a.drawn()).length, total: arts.length };
    },
    setArtificialDebug(next: {
      overMeasured?: boolean; inert?: boolean; reliefInert?: boolean; reliefUnconditioned?: boolean;
      reliefRepeat?: number;
    }): void {
      if (next.overMeasured === undefined && next.inert === undefined && next.reliefInert === undefined
        && next.reliefUnconditioned === undefined && next.reliefRepeat === undefined) return;
      for (const a of arts) {
        if (next.overMeasured !== undefined) a.setOverMeasured(next.overMeasured);
        if (next.inert !== undefined) a.setInert(next.inert);
      }
      for (const r of reliefs) {
        if (next.overMeasured !== undefined) r.setOverMeasured(next.overMeasured);
        if (next.reliefInert !== undefined) r.setInert(next.reliefInert);
      }
      if (next.reliefInert !== undefined) tileReliefInertDebug = next.reliefInert;
      if (next.reliefUnconditioned !== undefined) tileReliefUnconditionedDebug = next.reliefUnconditioned;
      if (next.reliefRepeat !== undefined) tileReliefRepeatDebug = Math.max(1, Math.round(next.reliefRepeat));
      for (const t of tileReliefSamplers) {
        if (next.reliefInert !== undefined) t.reliefInert = next.reliefInert;
        if (next.reliefUnconditioned !== undefined) t.reliefUnconditioned = next.reliefUnconditioned;
        if (next.reliefRepeat !== undefined) t.reliefRepeat = tileReliefRepeatDebug;
      }
      config.onNeedsRender();
    },
    setDrawRadius,
    setDatumRadius,
    resetDrawRadius(bodyId: string): void {
      const b = byId.get(bodyId);
      if (b) setDrawRadius(bodyId, shapeDatums.get(bodyId) ?? b.row.radius_m);
    },
    setShapeGeometry(
      bodyId: string, geometry: THREE.BufferGeometry | null, datumRadiusM?: number,
    ): void {
      const b = byId.get(bodyId);
      if (!b) return;
      if (geometry === null) shapeDatums.delete(bodyId);
      else if (datumRadiusM !== undefined) setDatumRadius(bodyId, datumRadiusM);
      const next = geometry ?? unshaped(bodyId);
      if (!next || b.mesh.geometry === next) return;
      if (geometry && !linked.has(bodyId)) {
        // The swap is a program link on the frame it lands; this is that frame.
        // The map's direction sampling is already on the material by here:
        // src/atlas.ts marks it and rebuilds the globe's one hook before it
        // hands the geometry over.
        linked.add(bodyId);
        config.linkPrograms(b.mesh);
      }
      b.mesh.geometry = next;
      config.onNeedsRender();
    },
    setGlobeSegments(bodyId: string, widthSegments: number, heightSegments: number): void {
      const b = byId.get(bodyId);
      const was = spheres.get(bodyId);
      if (!b || !was) return;
      const next = new THREE.SphereGeometry(b.row.radius_m, widthSegments, heightSegments);
      spheres.set(bodyId, next);
      // A body drawn as its own shape keeps that geometry; only the sphere
      // behind it is rebuilt, so putting the shape back finds the new one.
      if (b.mesh.geometry === was) b.mesh.geometry = next;
      was.dispose();
      const wasStandIn = standIns.get(bodyId);
      if (wasStandIn) {
        const nextStandIn = ellipsoidGeometry(b.row, widthSegments, heightSegments);
        standIns.set(bodyId, nextStandIn);
        if (b.mesh.geometry === wasStandIn) b.mesh.geometry = nextStandIn;
        wasStandIn.dispose();
      }
      config.onNeedsRender();
    },
    setRoundGlobes(on: boolean): void {
      if (roundGlobes === on) return;
      roundGlobes = on;
      for (const b of bodies) {
        const id = b.row.id;
        if (b.mesh.geometry === spheres.get(id) || b.mesh.geometry === standIns.get(id)) {
          b.mesh.geometry = unshaped(id) ?? b.mesh.geometry;
        }
      }
      config.onNeedsRender();
    },
    layoutLabels(camera: THREE.Camera, viewportH: number): void {
      if (labels.size === 0 || viewportH <= 0) return;
      camera.updateWorldMatrix(true, false);
      camera.getWorldQuaternion(_labelQ).invert();
      const p = (camera as THREE.PerspectiveCamera).projectionMatrix.elements;
      const p00 = p[0] ?? 1;
      const p11 = p[5] ?? 1;
      // The pane's width in the same pixels, taken off the projection's own
      // aspect: the shared canvas is wider than the stage the Atlas draws to,
      // and it is the stage the camera was given.
      const viewportW = viewportH * (p11 / p00);
      const halfW = viewportW * 0.5;
      const halfH = viewportH * 0.5;
      const cand: BodyLabel[] = [];
      for (const [id, lab] of labels) {
        const b = byId.get(id);
        // `surfaceGate` is the pre-fix rule: a name only on a row that can be
        // descended to, which is what left the gas giants unnamed.
        const off = labelSuppress || !b || !lab.gated
          || (labelSurfaceGate && !b.row.availability.descend);
        if (off) continue;
        _labelV
          .set(b.world.x - camWorld.x, b.world.y - camWorld.y, b.world.z - camWorld.z)
          .applyQuaternion(_labelQ);
        const z = -_labelV.z;
        if (!(z > 0)) continue;
        // A sprite with size attenuation off is scaled in radians, so its box
        // in pixels follows from the projection alone.
        const hw = 0.5 * lab.sprite.scale.x * p00 * halfW;
        const hh = 0.5 * lab.sprite.scale.y * p11 * halfH;
        const cx = ((_labelV.x * p00) / z * 0.5 + 0.5) * viewportW;
        // The anchor sits below the box (`center.y = -0.9`), by 1.4 of the
        // sprite's own heights.
        const cy = (1 - ((_labelV.y * p11) / z * 0.5 + 0.5)) * viewportH - 2.8 * hh;
        lab.left = cx - hw;
        lab.right = cx + hw;
        lab.top = cy - hh;
        lab.bottom = cy + hh;
        lab.nl = (lab.left / viewportW) * 2 - 1;
        lab.nr = (lab.right / viewportW) * 2 - 1;
        lab.nt = 1 - (lab.top / viewportH) * 2;
        lab.nb = 1 - (lab.bottom / viewportH) * 2;
        // A box wholly off the pane is not a name anybody reads, and it cannot
        // crowd one that is.
        if (lab.nr < -1 || lab.nl > 1 || lab.nt < -1 || lab.nb > 1) continue;
        lab.boxed = true;
        lab.sortKey = lab.trueHalfDeg;
        cand.push(lab);
      }
      const placed = declutterLabels(
        cand.map((l): LabelCandidate => ({
          id: l.id, left: l.left, top: l.top, right: l.right, bottom: l.bottom,
          priority: l.sortKey, subordinateTo: l.parent, fade: l.discFade,
        })),
        { marginShare: LABEL_CLEAR_MARGIN, rampShare: LABEL_CLEAR_RAMP },
      );
      for (let i = 0; i < cand.length; i++) {
        const lab = cand[i]!;
        const p2 = placed[i]!;
        lab.clearPx = p2.clearPx;
        lab.opacity = labelNoDeclutter ? lab.discFade : p2.opacity;
        lab.sprite.visible = lab.opacity > 0.004;
        lab.mat.opacity = lab.opacity;
      }
    },
    labelState(): readonly LabelState[] {
      const out: LabelState[] = [];
      for (const [id, l] of labels) {
        out.push({
          id, text: l.text, parent: l.parent,
          visible: l.sprite.visible,
          opacity: +l.opacity.toFixed(4),
          left: +l.left.toFixed(2), top: +l.top.toFixed(2),
          right: +l.right.toFixed(2), bottom: +l.bottom.toFixed(2),
          nl: +l.nl.toFixed(6), nr: +l.nr.toFixed(6),
          nt: +l.nt.toFixed(6), nb: +l.nb.toFixed(6),
          clearPx: +l.clearPx.toFixed(2),
          discFade: +l.discFade.toFixed(4),
          boxed: l.boxed,
        });
      }
      return out;
    },
    pickLabelAtNdc(x: number, y: number): string | null {
      let bestId: string | null = null;
      let bestOpacity = 0;
      for (const [id, l] of labels) {
        if (!l.boxed || !l.sprite.visible || l.opacity < LABEL_PICK_MIN_OPACITY) continue;
        if (x < l.nl || x > l.nr || y > l.nt || y < l.nb) continue;
        if (l.opacity <= bestOpacity) continue;
        bestId = id;
        bestOpacity = l.opacity;
      }
      return bestId;
    },
    setLabelDebug(next: {
      suppress?: boolean; noDeclutter?: boolean; surfaceGate?: boolean;
    }): void {
      if (next.suppress !== undefined) labelSuppress = next.suppress;
      if (next.noDeclutter !== undefined) labelNoDeclutter = next.noDeclutter;
      if (next.surfaceGate !== undefined) labelSurfaceGate = next.surfaceGate;
      for (const l of labels.values()) {
        if (labelSuppress) { l.sprite.visible = false; l.opacity = 0; }
      }
      config.onNeedsRender();
    },
    setNamesVisible(on: boolean): void {
      namesVisible = on;
    },
    layerCounts(): Record<OrreryLayer, { drawn: number; total: number }> {
      const z = (): { drawn: number; total: number } => ({ drawn: 0, total: 0 });
      const out: Record<OrreryLayer, { drawn: number; total: number }> = {
        orbitsHelio: z(), orbitsMoons: z(), spirals: z(), labels: z(), moonLabels: z(), rings: z(),
      };
      for (const b of bodies) {
        if (b.orbitLine) {
          const row = b.satellite ? out.orbitsMoons : out.orbitsHelio;
          row.total++;
          if (b.orbitLine.visible) row.drawn++;
        }
        const lab = labels.get(b.row.id);
        if (lab) {
          const row = b.satellite ? out.moonLabels : out.labels;
          row.total++;
          if (lab.sprite.visible && lab.opacity > 0) row.drawn++;
        }
      }
      for (const r of ringed) {
        out.rings.total++;
        if (r.rings.mesh.visible) out.rings.drawn++;
      }
      for (const sp of spirals) {
        out.spirals.total++;
        if (sp.line.visible) out.spirals.drawn++;
      }
      return out;
    },
    setLayerVisible(layer: OrreryLayer, on: boolean): void {
      if (layerOn[layer] === on) return;
      layerOn[layer] = on;
      config.onNeedsRender();
    },
    layerVisible(layer: OrreryLayer): boolean {
      return layerOn[layer];
    },
    dispose(): void {
      disposed = true;
      warmUp.length = 0;
      for (let i = teardown.length - 1; i >= 0; i--) {
        try { teardown[i]!(); } catch { /* a failed release must not skip the rest */ }
      }
      teardown.length = 0;
      bodies.length = 0;
      ringed.length = 0;
      spirals.length = 0;
      ringTracks.length = 0;
      byId.clear();
      labels.clear();
      spheres.clear();
      standIns.clear();
      linked.clear();
    },
  };
  return handle;
}
